/**
 * TanStack Query hooks — the ONLY way components read/write server state.
 *
 * Each hook builds a scope-prefixed key (queryKeys.ts) from the current session
 * scope so cache entries isolate per tenant. Mock fallbacks render every screen
 * without a live backend ONLY when VITE_USE_MOCKS is on (dev/demo builds); a live
 * build never substitutes mocks — transport failures propagate to the error/retry
 * UI so fabricated data is never shown as live production state.
 *
 * The exact endpoints these expect are documented in the README ("Backend API
 * contract") so the backend can match them 1:1.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./client";
import type { AssignResponse } from "@/lib/deviceMove";
import { qk, orgScopeKey } from "./queryKeys";
import type { DeviceListParams, ListParams, Paginated } from "./types";
import { useSession } from "@/stores/session";
import { shouldRetry } from "@/lib/queryClient";
import { PollTimeoutError, SWITCH_POLL_TIMEOUT_MS, withDeadline } from "@/lib/liveStatus";
// Switch CLI result sub-shapes live beside their (unit-tested) wording helpers.
import type { SwitchCliAccess, SwitchCliRejection, SwitchCliVia } from "@/lib/switchApplyResult";
import type { PortVlanDiff, PortVlansResponse } from "@/lib/portVlans";
import type { LivePortsResponse } from "@/lib/livePorts";
// Field-level config drift response (OP#1783).
import { normaliseDrift, type ConfigDrift } from "@/lib/configDrift";
// Port NAME auto-sync: badge mapping + batch shapes live beside their unit tests.
import { namesInFlight, type PortNameItem, type PortNamesBatchResponse, type PortNameSync } from "@/lib/portNameSync";
// Diagnostics tab shapes live beside their unit-tested helpers.
import type { DiagnosticCatalogue, DiagnosticResult } from "@/lib/diagnostics";
import type {
  ClientUsageResponse,
  FirmwareComplianceResponse,
  GatewayRoutesResponse,
  GatewayPostureResponse,
  Client,
  Customer,
  SitesOverviewResponse,
  AssignableDevice,
  DeviceSummary,
  SwitchPortsResponse,
  ConfigDiff,
  ConfigActionApplyResult,
  ConfigActionPreview,
  Device,
  DeviceConfigStatus,
  FleetStatusCounts,
  DeviceConnectivity,
  DeviceConfigActions,
  DevicePorts,
  ApPortsView,
  ApPortsPlan,
  ApPortsApplyResult,
  ApPortChange,
  DeviceSsids,
  DevicePhoto,
  DevicePhotoList,
  DeviceGateway,
  GatewayDhcp,
  GatewayFirewall,
  GatewayNetworks,
  GatewayPlan,
  GatewayApplyResult,
  GatewayIntent,
  GatewayChangeSpec,
  GatewayReassertPlan,
  SiteReassertPlan,
  SiteReassertRun,
  DeviceFirmware,
  DeviceFirmwareUpdate,
  SearchResults,
  AssistantAnswer,
  DeviceDiagnosis,
  AiProposal,
  AiProposalList,
  AiProposalStatus,
  ProposeActionBody,
  ConfirmProposalResult,
  ClientLocation,
  WiredNetwork,
  ApPortProfile,
  PortMapEntry,
  HealthWorklist,
  Me,
  SiteActionApplyResult,
  SiteActionPreview,
  SiteConfigActions,
  DashboardOverview,
  NotificationChannel,
  NotificationKind,
  SsidList,
  SsidSecurity,
  SsidSchedule,
  SsidFanoutResult,
  ConfigCompat,
  SsidPushStatus,
  SsidValidation,
  TagSsidChanges,
  ApplyTargetsPlan,
  RadioList,
  RadioFanoutResult,
  SiteTag,
  Prereg,
  PortalList,
  PortalFanoutResult,
  VoucherList,
  VoucherBatchResult,
  PskList,
  PskAddResult,
  RadiusServer,
  RadiusServerList,
  RadiusRealm,
  RadiusCredential,
  ProvisionResult,
  PasspointList,
  SiteWanWatchdog,
  WatchdogPolicyInput,
  WatchdogApplyResult,
  Operator,
  OperatorList,
  NetworkUser,
  SiteListRow,
  NetworkUserList,
  OperatorPreflight,
  OperatorSignOut,
  Role,
  ImportResult,
} from "@/types/domain";
import {
  MOCK_CLIENTS,
  MOCK_DEVICES,
  MOCK_ME,
  MOCK_WORKLIST,
  envelope,
  mockSitesOverview,
} from "./mock";

/** Whether to serve mock data instead of hitting the backend. */
const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === "true";

/** Serve `mock` ONLY when VITE_USE_MOCKS is explicitly on (dev/demo builds).
 *
 *  A live build NEVER substitutes mock fixtures: a transport failure (backend down / DNS /
 *  VPN drop) must propagate so TanStack Query retries and the UI shows its error/stale state.
 *  Previously we fell back to mocks on any transport error, which silently rendered fabricated
 *  data as LIVE production state (e.g. a stale "Live" dashboard, or the wrong device's identity
 *  next to a Reboot button) with no indication -- the exact demo-as-live hazard we must avoid. */
async function withMock<T>(real: () => Promise<T>, mock: () => T): Promise<T> {
  if (USE_MOCKS) return mock();
  return await real();
}

/* ------------------------------------------------------------------ *
 * MSP portal — customers (orgs) landing
 * ------------------------------------------------------------------ */

/** GET /api/v2/customers — the MSP landing feed (one row per customer + rollups). */
export function useCustomers() {
  return useQuery({
    queryKey: qk.customers(),
    queryFn: ({ signal }) =>
      api.get<{ items: Customer[]; count: number; total: number }>(
        "/customers",
        undefined,
        signal,
      ),
    // The MSP landing should feel current; refresh on a modest cadence.
    refetchInterval: 60_000,
  });
}

/**
 * GET /api/v2/sites/overview — the Customer Overview table: one row per site the caller can
 * see, with devices / offline / clients-now / alerts / firmware roll-ups.
 *
 * org_id = the header customer, so the table is THAT customer's sites (the endpoint narrows
 * within the caller's reach and 404s an out-of-scope org, same contract as the dashboard). A
 * site_operator gets only their own sites -- the server decides, not this hook.
 */
export function useSitesOverview() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sitesOverview(orgId),
    queryFn: ({ signal }) =>
      withMock<SitesOverviewResponse>(
        () => api.get<SitesOverviewResponse>("/sites/overview", { org_id: orgId }, signal),
        () => mockSitesOverview(orgId),
      ),
    enabled: !!orgId,
    // A triage table, not a live chart: the same modest cadence as the customers landing.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** POST /api/v2/orgs — create a customer (org). msp_admin only (backend re-checks).
 *  Refreshes /me too so the new customer (and its auto-created site) appear in the
 *  scope bar without a reload. */
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug?: string }) =>
      api.post<{ id: number; name: string; slug?: string | null }>("/orgs", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.customers() });
      void qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

/** GET /api/v2/orgs/{orgId}/sites — the sites (networks) under the current customer. */
export function useSites() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.list(orgId),
    queryFn: ({ signal }) =>
      api.get<{ items: SiteListRow[]; total: number }>(
        `/orgs/${orgId}/sites`,
        undefined,
        signal,
      ),
    enabled: !!orgId,
  });
}

/** POST /api/v2/orgs/{orgId}/sites — add a site to the current customer.
 *  Refreshes /me so the new site is selectable in the scope bar immediately. */
export function useCreateSite() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { name: string; location?: string }) =>
      api.post<{ id: number; name: string }>(`/orgs/${orgId}/sites`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.list(orgId) });
      void qc.invalidateQueries({ queryKey: qk.me() });
      void qc.invalidateQueries({ queryKey: qk.customers() });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Identity
 * ------------------------------------------------------------------ */

/** GET /api/v2/me — the Phase 0 hard blocker. Hydrates SessionContext. */
export function useMe() {
  const hydrate = useSession((s) => s.hydrate);
  return useQuery({
    queryKey: qk.me(),
    queryFn: async () => {
      const me = await withMock<Me>(() => api.get<Me>("/me"), () => MOCK_ME);
      hydrate(me);
      return me;
    },
    staleTime: Infinity, // identity is stable for the session
    retry: false, // a 401 here means "not logged in" — don't retry
  });
}

/* ------------------------------------------------------------------ *
 * Devices
 * ------------------------------------------------------------------ */

/**
 * GET /api/v2/devices — the device list.
 *
 * `q` (free-text over serial/hostname/model/IPv4/MAC) and `kind` (equipment type) are sent to
 * the SERVER, not applied to the returned page. This list is one page of at most 100 rows
 * ordered `last_seen DESC NULLS LAST`, so anything filtered in the browser can only ever
 * search the 100 most-recently-contacted devices — and a device an operator is hunting is
 * usually one that stopped checking in, i.e. exactly the rows that sort past the end of that
 * page. A client-side search box on a paged list is a search box that cannot find the thing
 * you opened it for. Same defect the status tiles had; same fix.
 */
export function useDevices(params: DeviceListParams = {}) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.devices.list(orgId, siteId, params),
    queryFn: ({ signal }) =>
      withMock<Paginated<Device>>(
        // org_id = the header customer (OP #1599). Without it, Site="All sites" sent NO scope
        // and an msp_admin got every customer's devices back -- the page then filtered one
        // 100-row page client-side, which both showed the wrong customer and could miss the
        // right one's devices. /devices honours ?org_id= only as a NARROWING inside g.auth
        // (see _filtered_device_query), so this can never widen what the caller sees. The
        // query key already carries orgId, so the cache stays per-customer.
        () => api.list<Device>("/devices", { site_id: siteId, org_id: orgId, ...params }, signal),
        // Offline/demo fallback: imitate the server's filters over the fixtures, so the page
        // does not silently ignore a search the moment the backend is unreachable.
        () => envelope(filterMockDevices(MOCK_DEVICES, siteId, params)),
      ),
    enabled: !!orgId,
    // Keep the previous rows on screen while a new search term or type filter loads. Without
    // this the grid drops to its skeleton on every debounced keystroke, which reads as the
    // list flickering empty and repeatedly "losing" the device being typed.
    placeholderData: (prev) => prev,
  });
}

/** Mock-mode twin of the server's device-list filters (site + q + kind). Dev/offline only. */
function filterMockDevices(
  devices: Device[],
  siteId: string | null,
  params: DeviceListParams,
): Device[] {
  const term = (params.q ?? "").trim().toLowerCase();
  // Separator-insensitive MAC comparison, mirroring _helpers.norm_mac on the backend.
  const normMac = (v: string) => v.toLowerCase().replace(/[^0-9a-z]/g, "");
  const normTerm = normMac(term);
  return devices.filter((d) => {
    if (siteId && d.site_id !== siteId) return false;
    if (params.kind && d.kind !== params.kind) return false;
    if (!term) return true;
    const haystack = [d.serial, d.name, d.model, d.ip ?? "", d.description ?? ""]
      .join(" ")
      .toLowerCase();
    return haystack.includes(term) || (!!normTerm && normMac(d.mac ?? "").includes(normTerm));
  });
}

/**
 * GET /api/v2/devices/status-counts — the four status tiles above the device list.
 *
 * WHY THIS IS A REQUEST AND NOT A `rows.filter(...).length`. It used to be the latter, and
 * that could not work: useDevices() fetches ONE PAGE (100 devices by default) ordered
 * `last_seen DESC NULLS LAST`, and that ordering sorts offline devices — and never-seen ones
 * especially — to the very bottom of the fleet. On a 300-device customer the tiles were
 * therefore computed from roughly the 100 healthiest devices, so the page could print
 * "0 Offline" with forty APs down. The tile whose whole job is to say "go look at these" was
 * structurally incapable of ever showing them.
 *
 * The filters are passed THROUGH to the server so the tiles describe exactly the rows the
 * list renders beneath them. A tile counting a different set than the table under it is a
 * subtler version of the same lie.
 */
export function useFleetStatusCounts(filters: {
  kind?: string;
  org_id?: string;
  site_id?: string;
  tag?: string;
  /** The list's search term. Passed through so a search narrows the tiles WITH the rows —
   *  a tile counting the whole fleet above a searched list is the same lie in a new place. */
  q?: string;
} = {}) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  // Drop empty values rather than sending `?tag=`: an empty filter means "no filter", and
  // the backend would read the empty string as a tag nothing carries.
  const query = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v != null && v !== ""),
  ) as Record<string, string>;
  return useQuery({
    queryKey: qk.devices.statusCounts(orgId, siteId, query),
    queryFn: ({ signal }) =>
      api.get<FleetStatusCounts>("/devices/status-counts", query, signal),
    enabled: !!orgId,
    // The same staleness the other two fleet sidecars use. These are counts an operator
    // glances at, not a live feed, and the endpoint scans the fleet to produce them.
    staleTime: 60_000,
  });
}

/**
 * GET /api/v2/config/verification — latest applied-state verdict for every device in scope.
 *
 * Fetched ONCE for the whole list and joined by serial, not per row: "which of my devices
 * are not running what we pushed?" is the question the fleet page exists to answer, and it
 * must not cost a request per device. Returns a Map so the column lookup is O(1).
 *
 * A device missing from the response is NOT "fine" — the caller must render it as unknown.
 * The backend already emits every in-scope device explicitly for this reason; the Map is a
 * second line of defence for the case where the two lists drift.
 */
export function useFleetConfigStatus() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.devices.configStatus(orgId, siteId),
    queryFn: async ({ signal }) => {
      const body = await api.list<DeviceConfigStatus>(
        "/config/verification", { site_id: siteId }, signal,
      );
      return new Map(body.items.map((i) => [i.serial, i]));
    },
    enabled: !!orgId,
    // Verification is a background sweep on the server, not a live feed. A minute-stale
    // verdict is fine; re-fetching per focus would hammer a cheap endpoint for no new data.
    staleTime: 60_000,
  });
}

/**
 * GET /api/v2/devices/connectivity — hourly up/gap/unknown buckets for every device in scope.
 *
 * Backs the per-row sparkline. Same one-request-per-page rule as useFleetConfigStatus.
 */
export function useFleetConnectivity(hours = 24) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.devices.connectivity(orgId, siteId, hours),
    queryFn: async ({ signal }) => {
      const body = await api.list<DeviceConnectivity>(
        "/devices/connectivity", { site_id: siteId, hours }, signal,
      );
      return new Map(body.items.map((i) => [i.serial, i]));
    },
    enabled: !!orgId,
    // Buckets are hourly, so anything finer than a minute is wasted work.
    staleTime: 60_000,
  });
}

/** GET /api/v2/devices/<serial>/connectivity — one device's timeline (device page). */
export function useDeviceConnectivity(serial: string, hours = 24) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.deviceConnectivity(orgId, serial, hours),
    queryFn: ({ signal }) =>
      api.get<DeviceConnectivity>(`/devices/${serial}/connectivity`, { hours }, signal),
    enabled: !!orgId && !!serial,
    staleTime: 60_000,
  });
}

/**
 * GET /api/v2/devices/<serial>/config/verification — one device's verdict (device page).
 *
 * Typed as the fleet row plus the extra detail the device page can afford to show.
 */
export function useDeviceConfigStatus(serial: string) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.verification(orgId, serial),
    queryFn: ({ signal }) =>
      api.get<{
        serial: string;
        verdict: DeviceConfigStatus["verdict"];
        coverage: DeviceConfigStatus["coverage"];
        change_scope: string | null;
        latest: (DeviceConfigStatus & {
          detail: string | null;
          mismatches: { path: string; expected: unknown; actual: unknown }[];
          checked_fields: number;
          observations: number;
        }) | null;
        // Hoisted staleness (config_verify.staleness): an offline device's verdict is its
        // last word and must not render as live green state.
        device_online?: boolean;
        stale?: boolean;
        stale_reason?: string | null;
        last_confirmed_at?: string | null;
        /** Whether site operations manage this device at all (services/config_compat). */
        config_compat?: ConfigCompat;
      }>(`/devices/${serial}/config/verification`, undefined, signal),
    enabled: !!orgId && !!serial,
    staleTime: 30_000,
  });
}

/**
 * GET /api/v2/devices/<serial>/config/drift -- field-level desired vs actual (OP#1783).
 *
 * Read-only on the server (no owgw call, nothing pushed). Rows are the same persisted
 * comparison the config badge counts; secrets arrive already masked for every role.
 */
export function useDeviceConfigDrift(serial: string) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.drift(orgId, serial),
    // Normalised here so every consumer gets complete lists, whatever the controller returned.
    queryFn: async ({ signal }) =>
      normaliseDrift(await api.get<Partial<ConfigDrift>>(`/devices/${serial}/config/drift`, undefined, signal)),
    enabled: !!orgId && !!serial,
    staleTime: 30_000,
  });
}

/**
 * POST /devices/{serial}/config/exclude | include -- the operator's "Exclude from Meridian
 * config" / "Include again" (org_admin+, audited). Include also sends the device the CURRENT
 * site SSIDs (`resync`), so it never comes back on a stale name; `resync_error` says when that
 * push could not run (the include itself still stands).
 */
export function useSetConfigExcluded(serial: string) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ exclude, note }: { exclude: boolean; note?: string }) =>
      api.post<{
        serial: string;
        config_compat: ConfigCompat;
        resync?: SsidFanoutResult;
        resync_error?: string;
      }>(`/devices/${serial}/config/${exclude ? "exclude" : "include"}`, note ? { note } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.verification(orgId, serial) });
      // The fleet config column and every site's SSID list carry the same state.
      void qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey.includes("config-status") ||
          (q.queryKey.includes("sites") && q.queryKey.includes("ssids")),
      });
    },
  });
}

/** GET /api/v2/devices/unassigned — the onboarding pool (org devices with no site). */
export function useUnassignedDevices() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.unassigned(orgId),
    queryFn: ({ signal }) =>
      api.get<{ items: Device[]; count: number; total: number }>(
        "/devices/unassigned",
        undefined,
        signal,
      ),
    enabled: !!orgId,
  });
}

/**
 * GET /api/v2/devices?org_id=<quarantine org> -- every device waiting in the quarantine holding
 * area (2026-10-19), for the Quarantine panel that replaces the Overview there.
 *
 * Deliberately NOT useDevices(): that adds the header SITE, and a quarantine device may sit
 * either site-less (owgw auto-adopt) or on the placeholder "Default Site" -- both are waiting.
 * The org filter only NARROWS inside g.auth (_filtered_device_query), so this cannot widen what
 * the caller sees; limit=500 is the server's own cap. The key lives under orgScopeKey(orgId),
 * so an assign (useAssignDevices invalidates that scope) refreshes the list.
 */
export function useQuarantineDevices(orgId: string | null) {
  return useQuery({
    queryKey: qk.devices.list(orgId, null, { quarantine: true, limit: 500 } as DeviceListParams),
    queryFn: ({ signal }) =>
      api.list<Device>("/devices", { org_id: orgId, limit: 500 }, signal),
    enabled: !!orgId,
    // A holding area changes when a unit is plugged in somewhere: the landing-page cadence.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** GET /api/v2/devices/assignable?site_id= — devices addable to a site (unassigned +
 *  placed elsewhere), each with its current placement for display. */
export function useAssignableDevices(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.assignable(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: AssignableDevice[]; count: number; total: number }>(
        "/devices/assignable",
        { site_id: siteId },
        signal,
      ),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/devices/assign — place devices into a site; invalidate org scope. */
export function useAssignDevices() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: {
      serials: string[];
      site_id: number;
      naming_pattern?: string;
      tags?: string[];
      // OP#1935: a MOVE (already-placed device) applies the customer's onboarding default only
      // when this is true; first claims are unaffected. See lib/deviceMove.
      apply_customer_default?: boolean;
    }) =>
      // firmware_queued: how many devices got an upgrade queued; results: per assigned device,
      // what was actually applied (rename / tags / firmware decision) -- lib/deviceMove.
      api.post<AssignResponse>("/devices/assign", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/**
 * GET /api/v2/devices/{serial}/switch/ports — live port view for a switch (owgw state, SNMP).
 *
 * OP#1860: `opts.offline` (the device list already says the switch is offline) skips the poll
 * entirely, and every poll is time-boxed (SWITCH_POLL_TIMEOUT_MS): a switch that does not
 * answer ends in PollTimeoutError -- NOT retried, so the card says "stopped responding" instead
 * of "Polling switch…" forever. The server also answers reason="offline" at once.
 */
export function useSwitchPorts(serial: string | undefined, opts: { offline?: boolean } = {}) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.switchPorts(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      withDeadline(
        (s) => api.get<SwitchPortsResponse>(`/devices/${serial}/switch/ports`, undefined, s),
        SWITCH_POLL_TIMEOUT_MS,
        signal,
      ),
    enabled: !!serial && !!orgId && !opts.offline,
    // SNMP polls take a moment; don't hammer.
    staleTime: 15_000,
    // A deadline miss is an answer ("stopped responding"), not a transient to retry 3x (that
    // would be 4 x 20 s of "Polling…"). Everything else keeps the app-wide retry rule.
    retry: (count, error) => !(error instanceof PollTimeoutError) && shouldRetry(count, error),
  });
}

/** Rich per-port switch detail (SNMP) — UniFi-grade columns for the switch detail screen. */
export interface SwitchPortDetail {
  ifindex: number;
  name: string;
  status: "up" | "down";
  speed_mbps: number | null;
  /** "full" / "half" when the transport reports it (uCentral link-state `duplex`). */
  duplex?: string | null;
  alias: string | null;
  descr: string | null;
  admin_up?: boolean;
  native_vlan?: number | null;
  poe_enabled?: boolean;
  rx_bytes?: number;
  tx_bytes?: number;
  connected_macs?: string[];
  neighbor?: string | null;
  /** Real PoE draw in milliwatts (uCentral only). SNMP could only say "PoE capable". */
  poe_power_mw?: number | null;
  poe_fault?: string | null;
  /** Per-port spanning-tree, for chasing a loop. Not available over SNMP. */
  stp_state?: string | null;
  stp_role?: string | null;
  /**
   * Port NAME auto-sync state (services/port_name_sync): does the switch hold `alias` yet?
   * null/absent = Meridian holds no name for this port. `alias` is Meridian's DESIRED name.
   */
  name_sync?: PortNameSync | null;
}
export interface SwitchDetailResponse {
  /** Retained for compatibility; now means "we have a port list", not "SNMP answered". */
  snmp_ok: boolean;
  /**
   * Which transport produced these ports.
   *   "ucentral"  the device's own outbound control channel — works behind NAT, so this is
   *               the only source that survives the switch leaving the lab
   *   "snmp"      controller -> device inbound UDP/161; flat-network only
   *   "connector" relayed by a Site Connector
   * Absent when there is no port list at all. Deliberately not the string "none": that is
   * truthy in JS and every `if (source)` guard would take the wrong branch.
   */
  source?: "ucentral" | "snmp" | "connector";
  switch: { serial: string; name: string; model: string | null; ip: string | null; mac: string | null };
  summary?: { total: number; up: number; down: number; poe: number };
  ports: SwitchPortDetail[];
}

/**
 * PUT /api/v2/devices/{serial}/switch/port-labels/{port} — name ONE port (msp_admin).
 *
 * Meridian is the source of truth for port names (owner decision 2026-09-14, option C): the name
 * is saved at once and the controller writes it to the switch description in the background,
 * so there is no preview step. `sync` is the new badge state; "" clears the name.
 */
export function useSetPortLabel(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: { port: string; label: string }) =>
      api.put<{ port: string; label: string | null; sync: PortNameSync | null; queued: boolean }>(
        `/devices/${serial}/switch/port-labels/${encodeURIComponent(vars.port)}`,
        { label: vars.label },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/**
 * GET /api/v2/devices/{serial}/switch/port-vlans — current VLANs per port: mode, untagged, tagged
 * list (with site VLAN names), STP edge/role/state, where each fact came from and its age, and
 * drift between live state and Meridian's intent (services/switch_port_vlans).
 */
export function usePortVlans(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.portVlans(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<PortVlansResponse>(`/devices/${serial}/switch/port-vlans`, undefined, signal),
    enabled: !!serial && !!orgId,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

/**
 * The same GET for several switches at once (site Ports table, OP#1819). Shares usePortVlans'
 * cache entries, so a switch open in the device sheet and the table never disagree.
 */
export function usePortVlansMany(serials: readonly string[]) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQueries({
    queries: serials.map((serial) => ({
      queryKey: qk.devices.portVlans(orgId, serial),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<PortVlansResponse>(`/devices/${serial}/switch/port-vlans`, undefined, signal),
      enabled: !!orgId,
      staleTime: 15_000,
      refetchInterval: 60_000,
    })),
  });
}

/** Result of POST switch/port-vlans/read: the read summary plus the refreshed view. */
export interface PortVlansReadResponse {
  read: {
    observed_at: string;
    port_count: number;
    vlan_count: number;
    skipped: string[];
    via: { gateway_serial: string | null; gateway_name: string | null } | null;
  };
  view: PortVlansResponse | null;
}

/**
 * POST /api/v2/devices/{serial}/switch/port-vlans/read — read per-port VLAN membership FROM THE
 * SWITCH (one read-only CLI session; org_admin and up; rate limited). Nothing is written to the
 * switch. Takes the serial per call so the site table can walk several switches with one hook.
 * Errors (409 SonicFi / CLI off / busy, 429, 502) throw ApiError with the server's message.
 */
export function useReadPortVlans() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.post<PortVlansReadResponse>(`/devices/${serial}/switch/port-vlans/read`, {}),
    onSuccess: (res, serial) => {
      // Seed the cache with the refreshed view, then let it refetch in the background.
      if (res.view) qc.setQueryData(qk.devices.portVlans(orgId, serial), res.view);
      void qc.invalidateQueries({ queryKey: qk.devices.portVlans(orgId, serial) });
    },
    onError: (_e, serial) => {
      // A failed attempt is recorded server-side (last_attempt): show it.
      void qc.invalidateQueries({ queryKey: qk.devices.portVlans(orgId, serial) });
    },
  });
}

/** GET /api/v2/devices/{serial}/switch/detail — rich per-port view (VLAN/PoE/MAC/Tx-Rx). */
export function useSwitchDetail(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.switchDetail(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<SwitchDetailResponse>(`/devices/${serial}/switch/detail`, undefined, signal),
    enabled: !!serial && !!orgId,
    staleTime: 15_000,
    // 5 s while a port name is queued or being written (the badge settles quickly), else 30 s.
    refetchInterval: (query) => (namesInFlight(query.state.data?.ports) ? 5_000 : 30_000),
  });
}

/**
 * useSwitchDetail for MANY switches at once -- the site-wide port table (routes/SwitchPorts).
 *
 * Same query key, fetcher and polling as useSwitchDetail, so the table and an open device sheet
 * share one cache entry per switch instead of polling twice. Results come back in `serials`
 * order; each entry is an independent query, so one unreachable switch does not blank the rest.
 */
export function useSwitchDetails(serials: readonly string[]) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQueries({
    queries: serials.map((serial) => ({
      queryKey: qk.devices.switchDetail(orgId, serial),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<SwitchDetailResponse>(`/devices/${serial}/switch/detail`, undefined, signal),
      enabled: !!orgId,
      staleTime: 15_000,
      // Same polling rule as useSwitchDetail: faster only while a name is on its way.
      refetchInterval: (query: { state: { data?: SwitchDetailResponse } }) =>
        namesInFlight(query.state.data?.ports) ? 5_000 : 30_000,
    })),
  });
}

/**
 * Serial-agnostic switch writes for the BULK port editor, which walks several switches in one
 * operation and so cannot use the per-serial mutation hooks.
 *
 * applyPorts has exactly usePortConfigApply's contract (postSwitchCli: a refused/failed session
 * resolves as a 502 result; 400/403/404/transport throw). It does not invalidate on its own: the
 * caller refreshes each switch once its apply is done. NAMES are not here -- they save through
 * useSetPortNames (one batch request for every port on every switch).
 */
export function useSwitchPortBatch() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return {
    applyPorts: (serial: string, vars: { changes: PortChange[]; save: boolean; dry_run: boolean }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/ports/apply`, {
        changes: vars.changes,
        save: vars.save,
        dry_run: vars.dry_run,
      }),
    refresh: (serial: string) =>
      qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial) }),
  };
}

/**
 * POST /api/v2/switch/port-names — BATCH port names across any number of switches (msp_admin).
 *
 * All-or-nothing on the server (one invalid item -> 400 listing every problem, nothing saved).
 * On success each touched switch's detail is refetched once, so the badges appear (Syncing) and
 * then settle through useSwitchDetail's fast polling.
 */
export function useSetPortNames() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (items: PortNameItem[]) => api.post<PortNamesBatchResponse>("/switch/port-names", { items }),
    onSuccess: (_res, items) => {
      for (const serial of new Set(items.map((i) => i.serial))) {
        void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial) });
      }
    },
  });
}

/** POST /api/v2/devices/{serial}/switch/name-sync/retry — retry failed / waiting names now (msp_admin). */
export function useRetryNameSync(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: () => api.post<{ retried: number }>(`/devices/${serial}/switch/name-sync/retry`, {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/**
 * POST /api/v2/devices/{serial}/switch/name-sync/write — "Write names to switch" (msp_admin).
 *
 * The explicit write: every imported, waiting or failed name on the switch goes out in one session
 * now. The only way names reach a switch that isn't assigned to a site, and how an imported
 * (pre-auto-sync, possibly stale) name is sent without retyping it.
 */
export function useWriteNames(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: () => api.post<{ written: number }>(`/devices/${serial}/switch/name-sync/write`, {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/**
 * POST to a switch CLI route, returning a FAILED apply as data rather than throwing.
 *
 * WHY: the switch write routes answer a refused / failed session with HTTP 502 and the full
 * apply result ({ok:false, error, rejected, partial, saved, stage, ...}). api.post throws
 * ApiError for any non-2xx, so every panel's `res.ok ? ... : res.error` branch was unreachable
 * and a refused change showed a generic "Request failed" -- or, on the device sheet, nothing at
 * all. A 502 whose body IS an apply result is resolved as that result; everything else (400
 * validation, 403, 404, transport) still throws and reaches the caller's error banner.
 */
async function postSwitchCli<T extends { ok: boolean }>(path: string, body: unknown): Promise<T> {
  try {
    return await api.post<T>(path, body);
  } catch (e) {
    const b = e instanceof ApiError ? (e.body as Partial<T> | null) : null;
    if (e instanceof ApiError && e.status === 502 && b && typeof b === "object" && typeof b.ok === "boolean") {
      return b as T;
    }
    throw e;
  }
}

/** One per-port change the "Port Config" form produces (mirrors services/switch_portconfig). */
export interface PortChange {
  port: string;
  name?: string;
  mode?: "access" | "trunk";
  access_vlan?: number | null;
  native_vlan?: number | null;
  tagged_vlans?: number[];
  remove_vlans?: number[];
  /** Trunk native change: the port's CURRENT native, removed after the new one is set. */
  previous_native_vlan?: number | null;
  poe?: boolean | null;
  enabled?: boolean | null;
  // Advanced (Edgecore ecCLI, verified on ECS2100): all optional, only sent when set.
  dot1x?: "auto" | "force-authorized" | "force-unauthorized";
  priority?: number | null;          // 802.1p default CoS 0-7
  lldp?: "both" | "tx" | "rx" | "off";
  /**
   * STP edge port: true -> `spanning-tree edge-port`, false -> `no spanning-tree edge-port`.
   * For gateway / AP / end-device ports only (forwards immediately after a link flap). NOT
   * hardware-verified yet -- the apply stops at the first refused line, before the save.
   */
  edge_port?: boolean | null;
  ingress_rate?: number | null;      // kbps; 0 clears
  egress_rate?: number | null;       // kbps; 0 clears
  voice_vlan?: "auto" | "none" | number;
  voice_vlan_rule?: ("oui" | "lldp")[] | "oui" | "lldp";
  voice_vlan_priority?: number | null;   // 0-7
}
export interface PortConfigResponse {
  serial: string;
  ok: boolean;
  dry_run: boolean;
  host?: string;
  commands: string[];
  /** VLANs the switch lacked, created first in the same session (null name = created unnamed). */
  create_vlans?: { vlan: number; name: string | null }[];
  wire?: string[];
  transcript: string | null;
  error: string | null;
  /**
   * Did the switch CONFIRM the persist? true = flash-write success marker seen; false = the save
   * was in the command set but not confirmed (may be lost on reboot); null/absent = no save in
   * this set, or the session never ran (services/switch_cli._save_outcome). `ok` says nothing
   * about this -- never print "saved" off `ok` alone.
   */
  saved?: boolean | null;
  /** true = some lines landed in running-config before a refusal (and were NOT saved). */
  partial?: boolean;
  /** Commands that landed before the first refusal, in order. */
  applied?: string[];
  /** Lines the switch refused ("% Invalid input ..."). */
  rejected?: SwitchCliRejection[];
  /** relay | gateway | switch -- where a failed session broke. */
  stage?: string | null;
  /** The gateway a relayed session went through (never a secret). */
  via?: SwitchCliVia | null;
  /** How Meridian reaches the switch: direct or via a gateway (preview = the path Apply uses). */
  access?: SwitchCliAccess | null;
  /** Dry run only: before -> after VLANs per port, with warnings (null when not computable). */
  vlan_diff?: PortVlanDiff[] | null;
}

/**
 * POST /api/v2/devices/{serial}/switch/ports/apply — render + (dry-run or apply) ecCLI.
 *
 * dry_run=true (the form's "Preview") returns the exact commands with no device contact;
 * dry_run=false ("Apply") runs them over one CLI session (backend-gated by SWITCH_CLI_ENABLED).
 * A successful non-dry apply invalidates the switch detail so the port table reflects reality.
 */
export function usePortConfigApply(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: { changes: PortChange[]; save?: boolean; dry_run?: boolean }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/ports/apply`, {
        changes: vars.changes,
        save: vars.save ?? true,
        dry_run: vars.dry_run ?? true,
      }),
    onSuccess: (res) => {
      // Only a real, successful apply changed the switch — refresh its detail then.
      if (res.ok && !res.dry_run) {
        void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
        // The port VLAN view replays Meridian's writes: it changes with this apply.
        void qc.invalidateQueries({ queryKey: qk.devices.portVlans(orgId, serial ?? "") });
      }
    },
  });
}

/** POST /api/v2/devices/{serial}/switch/voice-vlan/apply — switch-level voice VLAN (dry-run default). */
export function useSwitchVoiceVlan(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: { enabled?: boolean; vid?: number; aging?: number; save?: boolean; dry_run?: boolean }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/voice-vlan/apply`, {
        enabled: vars.enabled ?? true,
        vid: vars.vid,
        aging: vars.aging,
        save: vars.save ?? true,
        dry_run: vars.dry_run ?? true,
      }),
    onSuccess: (res) => {
      if (res.ok && !res.dry_run) void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/** Result of the switch-wide 802.1X route: the apply result plus the state that was requested. */
export interface SwitchDot1xResponse extends PortConfigResponse {
  enabled: boolean;
  save: boolean;
}

/**
 * POST /api/v2/devices/{serial}/switch/dot1x/apply — 802.1X on/off for the WHOLE switch.
 *
 * `enabled` is required and sent as a real boolean (the backend 400s anything else, so an
 * omitted field can never flip authentication). dry_run defaults true: the Preview. A real,
 * successful run invalidates switch detail -- LLDP neighbours can appear/disappear with 802.1X.
 */
export function useSwitchDot1x(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: { enabled: boolean; save?: boolean; dry_run?: boolean }) =>
      postSwitchCli<SwitchDot1xResponse>(`/devices/${serial}/switch/dot1x/apply`, {
        enabled: vars.enabled,
        save: vars.save ?? true,
        dry_run: vars.dry_run ?? true,
      }),
    onSuccess: (res) => {
      if (res.ok && !res.dry_run) void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/**
 * POST /api/v2/devices/{serial}/switch/save — `copy running-config startup-config` on its own.
 *
 * dry_run defaults true (shows the command + access path). Nothing in the port view changes on a
 * save, so there is nothing to invalidate; read `saved` for whether the switch confirmed it.
 */
export function useSwitchSave(serial: string | undefined) {
  return useMutation({
    mutationFn: (vars: { dry_run?: boolean }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/save`, {
        dry_run: vars.dry_run ?? true,
      }),
  });
}

/** POST /api/v2/devices/{serial}/switch/lag/apply — link-aggregation group (dry-run default). */
export function useSwitchLag(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: {
      group: number; members?: string[]; mode?: "static" | "active" | "passive";
      load_balance?: string; remove?: boolean; save?: boolean; dry_run?: boolean;
    }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/lag/apply`, {
        group: vars.group, members: vars.members, mode: vars.mode ?? "static",
        load_balance: vars.load_balance, remove: vars.remove ?? false,
        save: vars.save ?? true, dry_run: vars.dry_run ?? true,
      }),
    onSuccess: (res) => {
      if (res.ok && !res.dry_run) void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
    },
  });
}

/**
 * POST /api/v2/devices/{serial}/switch/ports/action — transient PoE action (UniFi-parity).
 *
 * "cycle" reboots a stuck PoE device by dropping then restoring power. dry_run=true previews
 * the commands; dry_run=false runs them (backend-gated by SWITCH_CLI_ENABLED). A real,
 * successful action invalidates switch detail so the port's PoE state refreshes.
 */
export function usePortPoeAction(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (vars: { port: string; action: "cycle" | "on" | "off"; dry_run?: boolean }) =>
      postSwitchCli<PortConfigResponse>(`/devices/${serial}/switch/ports/action`, {
        port: vars.port,
        action: vars.action,
        dry_run: vars.dry_run ?? true,
      }),
    onSuccess: (res) => {
      if (res.ok && !res.dry_run) {
        void qc.invalidateQueries({ queryKey: qk.devices.switchDetail(orgId, serial ?? "") });
        // The port VLAN view replays Meridian's writes: it changes with this apply.
        void qc.invalidateQueries({ queryKey: qk.devices.portVlans(orgId, serial ?? "") });
      }
    },
  });
}

/** GET /api/v2/device-summary — per-kind rollup for the org "Overview" cards. */
export function useDeviceSummary() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.devices.summary(orgId, siteId),
    queryFn: ({ signal }) =>
      // org_id = the header customer (OP#1625): the server only NARROWS on it.
      api.get<DeviceSummary>("/device-summary", { org_id: orgId, site_id: siteId }, signal),
    enabled: !!orgId,
  });
}

export function useDevice(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.detail(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      withMock<Device>(
        () => api.get<Device>(`/devices/${serial}`, undefined, signal),
        () => MOCK_DEVICES.find((d) => d.serial === serial) ?? MOCK_DEVICES[0]!,
      ),
    enabled: !!serial && !!orgId,
  });
}

/**
 * Reboot a device — a mutating, destructive action. RoleGate hides the trigger
 * for read_only; the backend re-checks scope. Offline this must be BLOCKED, not
 * queued (SyncQueue field-hazard rule) — enforced at the call site later.
 */
export function useRebootDevice() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (serial: string) => api.post<{ ok: boolean }>(`/devices/${serial}/reboot`),
    onSuccess: () => {
      // Refresh device + worklist views under the current org.
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/** PUT /api/v2/devices/{serial}/name — rename the device (display name). */
export function useRenameDevice(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (name: string) =>
      api.put<{ serial: string; name: string }>(`/devices/${serial}/name`, { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.detail(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Device configuration ACTIONS — the "Configuration" panel
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/config/actions — the catalog + applied state.
 *  No mock fallback: this is a live, owgw-backed surface, so a backend/transport
 *  error should surface (the panel shows it) rather than render fake actions. */
export function useDeviceConfigActions(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.configActions(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DeviceConfigActions>(`/devices/${serial}/config/actions`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/** POST .../{action_id}/preview — the redacted before/after diff, no write. */
export function usePreviewConfigAction(serial: string | undefined) {
  return useMutation({
    mutationFn: (actionId: string) =>
      api.post<ConfigActionPreview>(`/devices/${serial}/config/actions/${actionId}/preview`),
  });
}

/** POST .../{action_id}/apply — the accountable write (owgw push + audit).
 *  On success, invalidate the whole org scope so the device + its config-action
 *  state (and, once the AP re-reports, its LLDP fields) refresh. */
export function useApplyConfigAction(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (actionId: string) =>
      api.post<ConfigActionApplyResult>(`/devices/${serial}/config/actions/${actionId}/apply`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Device ports — wall-plate AP ethernet port config (uplink / access+VLAN)
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/ports — physical ports + role/VLAN from the live config. */
export function useDevicePorts(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.ports(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DevicePorts>(`/devices/${serial}/ports`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

// PUT /api/v2/devices/{serial}/ports (useSetDevicePort) was REMOVED with the route (OP#1823):
// it pushed AP port changes without the AP port plane's guards and nothing here called it.
// AP port changes go through /devices/{serial}/ap-ports/plan + /apply.

/* ------------------------------------------------------------------ *
 * Client location — "where is this MAC?" (the client sheet)
 * ------------------------------------------------------------------ */

/** GET /api/v2/clients/{mac}/location — live association, AP, switch/port, site, trail. */
export function useClientLocation(mac: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.clients.location(orgId, mac ?? ""),
    queryFn: ({ signal }) =>
      api.get<ClientLocation>(`/clients/${mac}/location`, undefined, signal),
    enabled: !!mac && !!orgId,
    // A troubleshooting view: keep the live association fresh while the sheet is open.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

/* ------------------------------------------------------------------ *
 * Global search — the command-palette backend (devices/clients/sites)
 * ------------------------------------------------------------------ */

/** GET /api/v2/search?q= — cross-org global search. Caller debounces `q`; disabled under 2 chars. */
export function useGlobalSearch(q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: qk.search(trimmed),
    queryFn: ({ signal }) => api.get<SearchResults>("/search", { q: trimmed }, signal),
    enabled: trimmed.length >= 2,
    staleTime: 15_000,      // results are stable enough to reuse across quick re-opens
    placeholderData: (prev) => prev,   // keep the last results visible while the next load runs
  });
}

/** POST /api/v2/assistant/ask — KB-grounded natural-language answer for the palette's Ask tab.
 *  A mutation, not a query: questions are submitted explicitly (Enter/Send), never auto-fired on
 *  keystroke the way search is. */
export function useAskAssistant() {
  return useMutation({
    mutationFn: (question: string) =>
      api.post<AssistantAnswer>("/assistant/ask", { question }),
  });
}

/** POST /api/v2/assistant/diagnose — Root-Cause Assistance for ONE device. A mutation, not a query:
 *  it reaches owgw for LIVE facts, so it is fired EXPLICITLY ("Diagnose" button), never on render.
 *  READ-ONLY / advisory — it changes nothing, so there is nothing to invalidate. */
export function useDiagnoseDevice() {
  return useMutation({
    mutationFn: (serial: string) =>
      api.post<DeviceDiagnosis>("/assistant/diagnose", { serial }),
  });
}

/* ------------------------------------------------------------------ *
 * Assistant confirm-gated ACTIONS (Phase 2) — propose -> confirm/reject
 *
 * The write half of the assistant (api/v2/assistant_proposals.py), modelled on useAskAssistant's
 * mutation style. An operator turns a gateway change into a reviewable AiProposal (/propose) that
 * carries a REAL diff; a human then CONFIRMS it — which pushes through the SAME manual gateway write
 * path a hand-edit uses — or REJECTS it. The UI only ever sends the {intent, spec} the manual form
 * already builds; the diff and the resolved target are produced server-side by deterministic Python.
 * ------------------------------------------------------------------ */

/** POST /api/v2/assistant/propose — file a PENDING proposal. SIDE-EFFECT-FREE server-side (it builds
 *  a diff; nothing is pushed to the device yet), so there is nothing to invalidate but the proposals
 *  list the new row belongs to. 201 AiProposal. */
export function useProposeAction() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: ProposeActionBody) => api.post<AiProposal>("/assistant/propose", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.assistant.proposals(orgId) });
    },
  });
}

/**
 * POST /api/v2/assistant/proposals/<id>/confirm — the accountable human push.
 *
 * 200 {proposal, result}; 409 on a non-PENDING proposal OR on live-state DRIFT (the body then
 * carries {error, proposal} with the now-terminal, _expired proposal — the caller/card renders that);
 * 403 role; 404 scope. A successful confirm pushed a real gateway change through the manual apply
 * path, so it invalidates that gateway's four config views — the exact set useApplyGatewayChange
 * invalidates — plus the proposals list.
 */
export function useConfirmProposal() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ConfirmProposalResult>(`/assistant/proposals/${id}/confirm`),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.assistant.proposals(orgId) });
      // The confirm wrote to one gateway — refresh the same views a manual apply does (VLANs, DHCP,
      // the read-only Gateway section and Firewall are all one document, three+ screens).
      const serial = res.proposal.proposed?.serial;
      if (serial) {
        void qc.invalidateQueries({ queryKey: qk.devices.gatewayNetworks(orgId, serial) });
        void qc.invalidateQueries({ queryKey: qk.devices.gatewayDhcp(orgId, serial) });
        void qc.invalidateQueries({ queryKey: qk.devices.gateway(orgId, serial) });
        void qc.invalidateQueries({ queryKey: qk.devices.gatewayFirewall(orgId, serial) });
      }
    },
  });
}

/** POST /api/v2/assistant/proposals/<id>/reject — human discard of a PENDING proposal (200 proposal;
 *  409 if already terminal). Audited as a human action server-side. */
export function useRejectProposal() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.post<AiProposal>(`/assistant/proposals/${id}/reject`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.assistant.proposals(orgId) });
    },
  });
}

/** GET /api/v2/assistant/proposals?kind=&status= — the org-scoped proposals list (newest first). */
export function useProposals(params: { kind?: string; status?: AiProposalStatus } = {}) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    // The filter params ride as a further key segment under the list prefix, so a kind/status change
    // refetches and an invalidation of qk.assistant.proposals(orgId) still clears every variant.
    queryKey: [...qk.assistant.proposals(orgId), params] as const,
    queryFn: ({ signal }) =>
      api.get<AiProposalList>("/assistant/proposals", { ...params }, signal),
    enabled: !!orgId,
  });
}

/** GET /api/v2/devices/{serial}/gateway — read-only WAN/LAN/VLAN/DHCP view of a uCentral gateway. */
export function useDeviceGateway(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.gateway(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DeviceGateway>(`/devices/${serial}/gateway`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/* ------------------------------------------------------------------ *
 * Gateway networks — the propose-then-confirm WRITE path (Gateway > VLANs)
 *
 * Deliberately TWO hooks, not one. usePlanGatewayChange is read-only and produces the token;
 * useApplyGatewayChange refuses without it. A single "save" hook would make it possible for a
 * screen to skip the preview, which is exactly what the constraint forbids: a push replaces
 * the gateway's whole config, and the WAN in that config is how we reach the box.
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/gateway/networks — the editable LAN/VLAN table. */
export function useGatewayNetworks(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.gatewayNetworks(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<GatewayNetworks>(`/devices/${serial}/gateway/networks`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/**
 * The same networks table for SEVERAL gateways at once (Gateway > VLANs derives each site VLAN's
 * "Applied" state from every site gateway's live config). Same query key as useGatewayNetworks,
 * so the per-gateway detail and the table share one cache entry per box.
 */
export function useGatewayNetworksMany(serials: string[]) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQueries({
    queries: serials.map((serial) => ({
      queryKey: qk.devices.gatewayNetworks(orgId, serial),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<GatewayNetworks>(`/devices/${serial}/gateway/networks`, undefined, signal),
      enabled: !!orgId,
    })),
  });
}

/** GET /api/v2/devices/{serial}/gateway/dhcp — scopes, fixed IPs and lease evidence. */
export function useGatewayDhcp(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.gatewayDhcp(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<GatewayDhcp>(`/devices/${serial}/gateway/dhcp`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/** PUT /api/v2/devices/{serial}/gateway/dhcp/labels — controller-side name for a fixed IP.
 *
 *  NOT a device write, so no plan/confirm: a uCentral reservation has no name field and the
 *  label never reaches the gateway. An empty label clears it. */
export function useSetGatewayDhcpLabel(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { interface: string; macaddr: string; label: string }) =>
      api.put<{ label: string | null }>(`/devices/${serial}/gateway/dhcp/labels`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.gatewayDhcp(orgId, serial ?? "") });
    },
  });
}

/** GET /api/v2/devices/{serial}/gateway/firewall — port forwards, isolation, blocks.
 *
 *  Reads the gateway's LIVE uCentral document, so this is the device's actual posture and
 *  not a controller-side intent record. Writes go through the shared plan/apply pair below
 *  with the firewall.* intents — there is no second write path. */
export function useGatewayFirewall(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.gatewayFirewall(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<GatewayFirewall>(`/devices/${serial}/gateway/firewall`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/** POST /api/v2/devices/{serial}/gateway/plan — PROPOSE. Writes nothing, anywhere.
 *
 *  Not a useQuery: a preview is an explicit operator gesture on a form they just filled in,
 *  not something to (re)fetch in the background against a live device. */
export function usePlanGatewayChange(serial: string | undefined) {
  return useMutation({
    mutationFn: (body: { intent: GatewayIntent; spec: GatewayChangeSpec }) =>
      api.post<GatewayPlan>(`/devices/${serial}/gateway/plan`, body),
  });
}

/** POST /api/v2/devices/{serial}/gateway/apply — CONFIRM. Pushes to the live device.
 *
 *  `plan_token` is mandatory and comes from the plan the operator actually reviewed. */
export function useApplyGatewayChange(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { intent: GatewayIntent; spec: GatewayChangeSpec; plan_token: string }) =>
      api.post<GatewayApplyResult>(`/devices/${serial}/gateway/apply`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.gatewayNetworks(orgId, serial ?? "") });
      // VLANs and DHCP are two views of ONE document: an apply from either must refresh both.
      void qc.invalidateQueries({ queryKey: qk.devices.gatewayDhcp(orgId, serial ?? "") });
      // The device sheet's read-only Gateway section renders the same interfaces.
      void qc.invalidateQueries({ queryKey: qk.devices.gateway(orgId, serial ?? "") });
      // ...and so does Firewall: one document, three screens. A port forward changes the
      // same interfaces block the VLANs screen renders, so none of them may go stale.
      void qc.invalidateQueries({ queryKey: qk.devices.gatewayFirewall(orgId, serial ?? "") });
    },
  });
}

/** POST /api/v2/devices/{serial}/gateway/reassert {dry_run: true} — PREVIEW a re-apply of
 *  the Meridian-managed settings (no user edit). Read-only on the server. */
export function usePlanGatewayReassert(serial: string | undefined) {
  return useMutation({
    mutationFn: () =>
      api.post<GatewayReassertPlan>(`/devices/${serial}/gateway/reassert`, { dry_run: true }),
  });
}

/** POST /api/v2/devices/{serial}/gateway/reassert {dry_run: false} — PUSH. Requires the
 *  preview's plan_token; `confirm_removals` only when the preview flagged unexpected removals. */
export function useApplyGatewayReassert(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { plan_token: string; confirm_removals: boolean }) =>
      api.post<GatewayApplyResult>(`/devices/${serial}/gateway/reassert`, {
        dry_run: false,
        ...body,
      }),
    onSuccess: () => {
      // One document behind every gateway screen: refresh the device sheet's view of it.
      void qc.invalidateQueries({ queryKey: qk.devices.gateway(orgId, serial ?? "") });
    },
  });
}

/** POST /api/v2/sites/{id}/gateways/reassert {dry_run: true} — per-gateway previews, in apply
 *  order (HA secondary first). Read-only. */
export function usePlanSiteReassert(siteId: string | null) {
  return useMutation({
    mutationFn: () =>
      api.post<SiteReassertPlan>(`/sites/${siteId}/gateways/reassert`, { dry_run: true }),
  });
}

/** POST /api/v2/sites/{id}/gateways/reassert {dry_run: false} — start the sequential run. */
export function useStartSiteReassert(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { plan_token: string; confirm_removals: boolean }) =>
      api.post<{ run_id: string; status: string }>(`/sites/${siteId}/gateways/reassert`, {
        dry_run: false,
        ...body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.gatewayReassertStatus(orgId, siteId) });
    },
  });
}

/** GET /api/v2/sites/{id}/gateways/reassert/status — newest run; polls while it runs (each
 *  gateway can take several minutes: push, ~4 min AP disruption, new generation reported). */
export function useSiteReassertStatus(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.gatewayReassertStatus(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ run: SiteReassertRun | null }>(
        `/sites/${siteId}/gateways/reassert/status`,
        undefined,
        signal,
      ),
    enabled: !!siteId && !!orgId,
    refetchInterval: (query) => (query.state.data?.run?.status === "running" ? 10_000 : false),
    refetchIntervalInBackground: false,
  });
}

/** GET /api/v2/devices/{serial}/ssids — the SSIDs a single AP is broadcasting (live config). */
export function useDeviceSsids(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.ssids(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DeviceSsids>(`/devices/${serial}/ssids`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/* ------------------------------------------------------------------ *
 * Firmware — current version, available baseline, and upgrade execution
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/firmware — panel data (version, baseline, live update status). */
export function useDeviceFirmware(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.firmware(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DeviceFirmware>(`/devices/${serial}/firmware`, undefined, signal),
    enabled: !!serial && !!orgId,
    // Poll while an upgrade is in flight so the status/progress advances without a manual refresh.
    refetchInterval: (query) => {
      const s = query.state.data?.latest_update?.status;
      return s === "pending" || s === "downloading" || s === "applying" ? 5000 : false;
    },
  });
}

/** POST /api/v2/devices/{serial}/firmware/upgrade — queue (and, if enabled, issue) an upgrade. */
export function useUpgradeFirmware(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { firmware_id?: number; channel?: "stable" }) =>
      api.post<{ update: DeviceFirmwareUpdate; issued: boolean }>(
        `/devices/${serial}/firmware/upgrade`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.firmware(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * AP port profiles — named wired networks + reusable per-port maps
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/wired-networks — named VLANs at the site. */
export function useWiredNetworks(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.wiredNetworks(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: WiredNetwork[]; count: number }>(
        `/sites/${siteId}/wired-networks`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

export function useCreateWiredNetwork(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { name: string; vlan_id: number }) =>
      api.post<WiredNetwork>(`/sites/${siteId}/wired-networks`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.wiredNetworks(orgId, siteId) }),
  });
}

export function useDeleteWiredNetwork(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ deleted: number }>(`/wired-networks/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.wiredNetworks(orgId, siteId) }),
  });
}

/** GET /api/v2/sites/{siteId}/port-profiles — reusable AP port profiles. */
export function usePortProfiles(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.portProfiles(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: ApPortProfile[]; count: number }>(
        `/sites/${siteId}/port-profiles`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

export function useCreatePortProfile(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { name: string; ports: Record<string, PortMapEntry> }) =>
      api.post<ApPortProfile>(`/sites/${siteId}/port-profiles`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.portProfiles(orgId, siteId) }),
  });
}

export function useUpdatePortProfile(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; ports?: Record<string, PortMapEntry> }) =>
      api.put<ApPortProfile>(`/port-profiles/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.portProfiles(orgId, siteId) }),
  });
}

export function useDeletePortProfile(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ deleted: number }>(`/port-profiles/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.portProfiles(orgId, siteId) }),
  });
}

/* ------------------------------------------------------------------ *
 * AP PORT PLANE — read, then PROPOSE, then CONFIRM
 *
 * Three hooks, and deliberately not one "save": a uCentral push replaces the AP's entire
 * configuration, so usePlanApPorts is read-only and produces the token that useApplyApPorts
 * refuses to go without. A single mutation would make it possible for a screen to skip the
 * preview, which is exactly what the constraint forbids on live APs carrying a lab.
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/ap-ports — ports + effective setting + which layer set it. */
export function useApPorts(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.apPorts(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<ApPortsView>(`/devices/${serial}/ap-ports`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/**
 * GET /api/v2/devices/{serial}/ports/live — the LIVE per-port view (APs and switches, one shape):
 * link/speed, configured mode + VLANs, learned clients/MACs, LLDP, traffic + rate, with the state
 * report's age. Read-only, site_operator+ (a 403 renders as "requires site operator").
 * Polls at the state cadence (~60 s): faster would only re-read the same report.
 */
export function useLivePorts(serial: string | undefined, enabled = true) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.livePorts(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<LivePortsResponse>(`/devices/${serial}/ports/live`, undefined, signal),
    enabled: enabled && !!serial && !!orgId,
    staleTime: 20_000,
    refetchInterval: 60_000,
    // A 403/404 will not fix itself on retry.
    retry: (count, err) => {
      const status = (err as unknown as { status?: number })?.status;
      return count < 2 && status !== 403 && status !== 404;
    },
  });
}

/** POST /api/v2/devices/{serial}/ap-ports/plan — PROPOSE. Writes nothing, anywhere. */
export function usePlanApPorts(serial: string | undefined) {
  return useMutation({
    mutationFn: (body: { changes: ApPortChange[] }) =>
      api.post<ApPortsPlan>(`/devices/${serial}/ap-ports/plan`, body),
  });
}

/** POST /api/v2/devices/{serial}/ap-ports/apply — CONFIRM. Pushes to the live AP. */
export function useApplyApPorts(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { changes: ApPortChange[]; plan_token: string }) =>
      api.post<ApPortsApplyResult>(`/devices/${serial}/ap-ports/apply`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.apPorts(orgId, serial ?? "") });
      // The legacy per-port panel reads the same interfaces off the same config.
      void qc.invalidateQueries({ queryKey: qk.devices.ports(orgId, serial ?? "") });
      // The live view reads the configured mode/VLANs from the config of record.
      void qc.invalidateQueries({ queryKey: qk.devices.livePorts(orgId, serial ?? "") });
    },
  });
}

/** POST /api/v2/port-profiles/{id}/default — make this the site's default profile. */
export function useMakeDefaultPortProfile(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.post<ApPortProfile>(`/port-profiles/${id}/default`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sites.portProfiles(orgId, siteId) }),
  });
}

/** POST /api/v2/devices/{serial}/port-profile — apply a profile to an AP (owgw push + audit).
 *  `warnings` carries the soft-skip notes (ports the profile has that this AP lacks) and any
 *  uplink allow-list cautions, so the caller can surface exactly what did and did not apply. */
export function useAssignPortProfile() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ serial, profile_id }: { serial: string; profile_id: number }) =>
      api.post<{ serial: string; profile_id: number; changed: boolean; warnings?: string[] }>(
        `/devices/${serial}/port-profile`, { profile_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) }),
  });
}

/** DELETE /api/v2/devices/{serial}/port-profile — clear the assignment. Leaves the live config
 *  as-is (does NOT revert the AP); the profile is simply no longer the base layer. */
export function useClearPortProfile() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (serial: string) =>
      api.del<{ serial: string; profile_id: number | null }>(`/devices/${serial}/port-profile`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) }),
  });
}

/* ------------------------------------------------------------------ *
 * SSIDs — Wireless > SSIDs (site-scoped, PSK write-only)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/ssids — the site's SSIDs (PSK never returned). */
export function useSsids(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.ssids(orgId, siteId),
    queryFn: ({ signal }) => api.get<SsidList>(`/sites/${siteId}/ssids`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/ssids — create/update an SSID (fan-out). */
export function useUpsertSsid(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: {
      name: string;
      bands: string[];
      security: SsidSecurity;
      passphrase?: string;
      hidden?: boolean;
      pmf?: string;            // 802.11w mode: "disabled" | "optional" | "required"
      max_clients?: number | null;  // per-SSID client cap (null/0 = unlimited)
      vlan?: number | null;
      isolate?: boolean;
      unicast_conversion?: boolean;
      rate_down?: number | null;
      rate_up?: number | null;
      radius_host?: string;
      radius_port?: number;
      radius_secret?: string;
      radius_acct_host?: string;
      radius_acct_port?: number;
      radius_acct_secret?: string;
      radius_server_id?: number;
      target?: string;
      schedule?: SsidSchedule; // broadcast schedule (enabled=false => always on)
      /** The operator has read the backend's warnings (409 needs_confirmation) — proceed. */
      acknowledge_warnings?: boolean;
      /** The operator ticked "these APs will broadcast no Wi-Fi" (a hard warning, OP#1778). */
      acknowledge_no_ssid_aps?: boolean;
    }) => api.post<SsidFanoutResult>(`/sites/${siteId}/ssids`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.ssids(orgId, siteId) }),
  });
}

/**
 * POST /api/v2/sites/{siteId}/ssids/validate — dry-run the Edit SSID save (no writes).
 *
 * Called BEFORE a rename: the rename lands first, so a refusal of the other fields found only
 * afterwards would leave the operator half-saved.
 */
export function useValidateSsid(siteId: string | null) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<SsidValidation>(`/sites/${siteId}/ssids/validate`, body),
  });
}

/**
 * GET /api/v2/sites/{siteId}/ssids/push-status?serial=… — where "still applying" pushes stand.
 *
 * Polls every 5s while `enabled`. The caller stops it once nothing is pending or the settle
 * horizon (3 min, matching the backend's SSID_PUSH_SETTLE_SECONDS default) has passed.
 */
export function useSsidPushStatus(siteId: string | null, serials: string[], enabled: boolean) {
  const orgId = useSession((s) => s.currentOrgId);
  const key = [...serials].sort().join(",");
  return useQuery({
    queryKey: [...qk.sites.ssids(orgId, siteId), "push-status", key] as const,
    queryFn: ({ signal }) =>
      api.get<SsidPushStatus>(`/sites/${siteId}/ssids/push-status?serial=${serials
        .map(encodeURIComponent)
        .join("&serial=")}`, undefined, signal),
    enabled: enabled && !!siteId && !!orgId && serials.length > 0,
    refetchInterval: enabled ? 5000 : false,
  });
}

/**
 * POST /api/v2/sites/{siteId}/ssids/{name}/rename — rename an SSID in place.
 *
 * Separate from useUpsertSsid because the backend treats it as a different operation: the
 * SSID has no id, so a rename re-keys the config plus the target/schedule/PSK stores, and
 * every client associated to the old name is disconnected. The caller must confirm first.
 */
export function useRenameSsid(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ name, newName }: { name: string; newName: string }) =>
      api.post<
        SsidFanoutResult & {
          name: string;
          previous_name: string;
          /** The config already carried the new name; only the stored rows needed moving. */
          already_done?: boolean;
          /**
           * The rename did not reach every AP. Reported with a 200, NOT an error: raising
           * would make the API layer roll back the audit rows for the APs that did move.
           * When true, `name` is still the OLD name (it is what describes the site) and
           * `requested_name` is what was asked for.
           */
          partial?: boolean;
          requested_name?: string;
        }
      >(`/sites/${siteId}/ssids/${encodeURIComponent(name)}/rename`, { new_name: newName }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.ssids(orgId, siteId) }),
  });
}

/** DELETE /api/v2/sites/{siteId}/ssids/{name} — remove an SSID (fan-out). */
export function useDeleteSsid(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (name: string) =>
      api.del<SsidFanoutResult>(`/sites/${siteId}/ssids/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.ssids(orgId, siteId) }),
  });
}

/** POST .../ssids/{name}/{enable|disable} — toggle an SSID on/off (keeps its definition). */
export function useToggleSsid(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ name, enable }: { name: string; enable: boolean }) =>
      api.post<{ name: string; enabled: boolean }>(
        `/sites/${siteId}/ssids/${encodeURIComponent(name)}/${enable ? "enable" : "disable"}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.ssids(orgId, siteId) }),
  });
}

/* ------------------------------------------------------------------ *
 * AP tags / groups
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/tags — the site's tags (groups) + AP counts. */
export function useSiteTags(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.tags(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ tags: SiteTag[] }>(`/sites/${siteId}/tags`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** GET /api/v2/devices/{serial}/tags — an AP's tags. */
export function useDeviceTags(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.tags(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<{ serial: string; tags: string[] }>(`/devices/${serial}/tags`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/** PUT /api/v2/devices/{serial}/tags — replace an AP's tags; refresh org scope. */
export function useSetDeviceTags(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (tags: string[]) =>
      api.put<{ serial: string; tags: string[]; before?: string[]; ssid_changes?: TagSsidChanges }>(
        `/devices/${serial}/tags`,
        { tags },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.tags(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/**
 * POST /api/v2/devices/{serial}/ssids/apply-targets — "Apply this group's SSIDs now" after a
 * tag change (OP#1778). `dry_run` returns the plan only; otherwise ONE config push to this AP
 * (~4 min Wi-Fi disruption on it). 409 when the AP would be left with no SSID and
 * `acknowledge` is not set.
 */
export function useApplyTargetSsids(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { dry_run?: boolean; acknowledge?: boolean }) =>
      api.post<Partial<SsidFanoutResult> & { dry_run?: boolean; plan: ApplyTargetsPlan }>(
        `/devices/${serial}/ssids/apply-targets`,
        body,
      ),
    onSuccess: (res) => {
      if (res.dry_run) return;
      void qc.invalidateQueries({ queryKey: qk.devices.ssids(orgId, serial ?? "") });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Device installer note + photos: the sheet's "Location & photos"
 * (api/v2/device_photos.py). No mock fallback: live, per-device data.
 * ------------------------------------------------------------------ */

/** GET /api/v2/devices/{serial}/photos: metadata only; images load via their own URLs. */
export function useDevicePhotos(serial: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.devices.photos(orgId, serial ?? ""),
    queryFn: ({ signal }) =>
      api.get<DevicePhotoList>(`/devices/${serial}/photos`, undefined, signal),
    enabled: !!serial && !!orgId,
  });
}

/** PUT /api/v2/devices/{serial}/description: set (or clear with null) the installer note. */
export function useSetDeviceDescription(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (description: string | null) =>
      api.put<{ serial: string; description: string | null; changed: boolean }>(
        `/devices/${serial}/description`,
        { description },
      ),
    onSuccess: () => {
      // Detail (the sheet) + org scope (the list's secondary line and its search).
      void qc.invalidateQueries({ queryKey: qk.devices.detail(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/** POST /api/v2/devices/{serial}/photos (multipart) with upload progress. */
export function useUploadDevicePhoto(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (args: {
      blob: Blob;
      filename: string;
      caption?: string;
      onProgress?: (loaded: number, total: number) => void;
    }) => {
      const form = new FormData();
      form.append("file", args.blob, args.filename);
      if (args.caption) form.append("caption", args.caption);
      return api.upload<DevicePhoto>(`/devices/${serial}/photos`, form, {
        onProgress: args.onProgress,
      });
    },
    // Settled, not success: a "limit reached" 400 also means the strip is stale.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.photos(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/** PUT /api/v2/devices/{serial}/photos/{id}: set (or clear with null) a caption. */
export function useSetPhotoCaption(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, caption }: { id: number; caption: string | null }) =>
      api.put<DevicePhoto>(`/devices/${serial}/photos/${id}`, { caption }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.photos(orgId, serial ?? "") });
    },
  });
}

/** DELETE /api/v2/devices/{serial}/photos/{id}: frees the slot. */
export function useDeleteDevicePhoto(serial: string | undefined) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) =>
      api.del<{ deleted: number; serial: string }>(`/devices/${serial}/photos/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices.photos(orgId, serial ?? "") });
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/**
 * POST /api/v2/devices/tags/bulk — add/remove tags across many APs at once.
 * ADD-only never wipes an AP's existing tags. Backs the device-list bulk "tag selection".
 */
export function useBulkTag() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { serials: string[]; add?: string[]; remove?: string[] }) =>
      api.post<{
        updated: number;
        added: number;
        removed: number;
        serials: string[];
        /** APs whose group-targeted SSIDs this change moved (OP#1778); nothing is pushed. */
        ssid_changes?: ({ serial: string } & TagSsidChanges)[];
      }>(
        "/devices/tags/bulk",
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Device pre-registration (reserve MAC/serial -> auto-claim on arrival)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/preregistrations — the site's reservations. */
export function usePreregs(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.preregs(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: Prereg[]; count: number }>(
        `/sites/${siteId}/preregistrations`,
        undefined,
        signal,
      ),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/preregistrations — reserve MAC/serials to the site. */
export function useAddPreregs(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { identifiers: string[]; note?: string }) =>
      api.post<{ added: number; skipped: number; items: Prereg[] }>(
        `/sites/${siteId}/preregistrations`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.preregs(orgId, siteId) });
    },
  });
}

/** DELETE /api/v2/preregistrations/{id} — remove one reservation. */
export function useDeletePrereg(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ deleted: number }>(`/preregistrations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.preregs(orgId, siteId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Hospitality — Guest / captive Portal (site-scoped, per SSID)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/portals — the site's SSIDs + captive-portal state. */
/**
 * GET /api/v2/clients/usage — top talkers in the current scope.
 *
 * Live AP state, so it is polled rather than cached indefinitely; `threshold_mbps` is optional
 * and only drives flagging.
 */
export function useClientUsage(
  siteId: string | null,
  opts: { top?: number; thresholdMbps?: number | null } = {},
) {
  const orgId = useSession((s) => s.currentOrgId);
  const params: Record<string, string | number> = {};
  // org_id = the header customer (OP#1625, the OP#1599 pattern): the server only NARROWS on it
  // and 404s an org outside the caller's reach. Without it a platform_admin saw every customer.
  if (orgId) params.org_id = orgId;
  if (siteId) params.site_id = siteId;
  if (opts.top) params.top = opts.top;
  if (opts.thresholdMbps != null) params.threshold_mbps = opts.thresholdMbps;
  return useQuery({
    queryKey: qk.sites.clientUsage(orgId, siteId, params as Record<string, unknown>),
    queryFn: ({ signal }) =>
      api.get<ClientUsageResponse>("/clients/usage", params, signal),
    enabled: !!orgId,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * GET /api/v2/firmware/compliance — drift against the approved image per device type.
 *
 * Derived from device rows, not a live device poll, so a modest staleTime is plenty.
 */
export function useFirmwareCompliance(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  const params: Record<string, string | number> = {};
  // org_id = the header customer (OP #1599 pattern, OP#1921). Without it a platform_admin's
  // compliance card counted every customer's devices. The server only NARROWS on it.
  if (orgId) params.org_id = orgId;
  if (siteId) params.site_id = siteId;
  return useQuery({
    queryKey: qk.sites.firmwareCompliance(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<FirmwareComplianceResponse>("/firmware/compliance", params, signal),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

/**
 * GET /api/v2/sites/{siteId}/gateway-routes — live routing tables from the site's gateways.
 *
 * Each call costs an SSH round trip to every gateway, so this deliberately does NOT poll and
 * does not refetch on window focus: the screen fetches once and offers an explicit Refresh.
 * `staleTime: Infinity` keeps a tab switch from silently re-running SSH sessions.
 */
export function useGatewayRoutes(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.gatewayRoutes(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<GatewayRoutesResponse>(`/sites/${siteId}/gateway-routes`, undefined, signal),
    enabled: !!siteId && !!orgId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * GET /api/v2/sites/{siteId}/gateway-posture — aggregated security-posture findings.
 *
 * A read-time aggregation of the firewall, routes and WAN-watchdog reads. The routes half costs
 * an SSH round trip per gateway, so — like useGatewayRoutes — this deliberately does NOT poll and
 * does not refetch on focus: the screen fetches once and offers an explicit Refresh.
 */
export function useGatewayPosture(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.gatewayPosture(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<GatewayPostureResponse>(`/sites/${siteId}/gateway-posture`, undefined, signal),
    enabled: !!siteId && !!orgId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function usePortals(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.portals(orgId, siteId),
    queryFn: ({ signal }) => api.get<PortalList>(`/sites/${siteId}/portals`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/portals/{ssid} — enable/disable + configure a portal. */
export function useSetPortal(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({
      ssid,
      ...body
    }: {
      ssid: string;
      enabled: boolean;
      auth_mode?: string;
      walled_garden?: string[];
      radius_host?: string;
      radius_port?: number;
      radius_secret?: string;
      radius_server_id?: number;
      branding?: Record<string, string>;
      idle_timeout?: number;
      session_timeout?: number;
    }) => api.post<PortalFanoutResult>(`/sites/${siteId}/portals/${encodeURIComponent(ssid)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.portals(orgId, siteId) }),
  });
}

/** GET /api/v2/sites/{siteId}/vouchers — the site's guest access codes. */
export function useVouchers(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.vouchers(orgId, siteId),
    queryFn: ({ signal }) => api.get<VoucherList>(`/sites/${siteId}/vouchers`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/vouchers/generate — mint a batch of codes. */
export function useGenerateVouchers(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: {
      count: number;
      duration_minutes?: number | null;
      data_cap_mb?: number | null;
      label?: string;
      prefix?: string;
    }) => api.post<VoucherBatchResult>(`/sites/${siteId}/vouchers/generate`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.vouchers(orgId, siteId) }),
  });
}

/** POST /api/v2/sites/{siteId}/vouchers/{code}/revoke — cancel a single code. */
export function useRevokeVoucher(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ code: string; state: string }>(
        `/sites/${siteId}/vouchers/${encodeURIComponent(code)}/revoke`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.vouchers(orgId, siteId) }),
  });
}

/** GET /api/v2/sites/{siteId}/ssids/{ssid}/psks — Multi-PSK entries on one SSID. */
export function useSitePsks(siteId: string | null, ssid: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.psks(orgId, siteId, ssid),
    queryFn: ({ signal }) =>
      api.get<PskList>(`/sites/${siteId}/ssids/${encodeURIComponent(ssid ?? "")}/psks`, undefined, signal),
    enabled: !!siteId && !!orgId && !!ssid,
  });
}

/** POST /api/v2/sites/{siteId}/ssids/{ssid}/psks — add/generate a PSK (key returned once). */
export function useAddPsk(siteId: string | null, ssid: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { label?: string; key?: string; mac?: string; vlan_id?: number | null }) =>
      api.post<PskAddResult>(`/sites/${siteId}/ssids/${encodeURIComponent(ssid ?? "")}/psks`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.psks(orgId, siteId, ssid) }),
  });
}

/** DELETE /api/v2/sites/{siteId}/ssids/{ssid}/psks/{id} — remove one PSK. */
export function useRemovePsk(siteId: string | null, ssid: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) =>
      api.del<{ id: number }>(`/sites/${siteId}/ssids/${encodeURIComponent(ssid ?? "")}/psks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.psks(orgId, siteId, ssid) }),
  });
}

/* ---- Passpoint / Hotspot 2.0 ---------------------------------------------- */

/** GET /api/v2/sites/{siteId}/passpoint — SSIDs + their Passpoint state. */
export function usePasspoint(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.passpoint(orgId, siteId),
    queryFn: ({ signal }) => api.get<PasspointList>(`/sites/${siteId}/passpoint`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/passpoint/{ssid} — enable/disable + configure Passpoint. */
export function useSetPasspoint(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({
      ssid,
      ...body
    }: {
      ssid: string;
      enabled: boolean;
      venue_name?: string;
      venue_group?: number | null;
      venue_type?: number | null;
      domain_name?: string[];
      roaming_consortium?: string[];
      nai_realm?: { realm: string; method: string }[];
      friendly_name?: string;
      /** ISO-639-2 code for the venue/operator names (default "eng"). */
      venue_language?: string;
      venue_url?: string;
      internet?: boolean;
    }) => api.post<PortalFanoutResult>(`/sites/${siteId}/passpoint/${encodeURIComponent(ssid)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.passpoint(orgId, siteId) }),
  });
}

/* ---- RADIUS Servers registry (BYO) ---------------------------------------- */

/** GET /api/v2/radius-servers — the org's registered RADIUS servers. */
export function useRadiusServers() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.radiusServers(orgId),
    queryFn: ({ signal }) => api.get<RadiusServerList>(`/radius-servers`, undefined, signal),
    enabled: !!orgId,
  });
}

type RadiusServerBody = {
  name: string;
  auth_host: string;
  auth_port?: number;
  acct_host?: string | null;
  acct_port?: number | null;
  secret?: string;
};

/** POST /api/v2/radius-servers — register a server (secret write-only). */
export function useCreateRadiusServer() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: RadiusServerBody) => api.post<RadiusServer>(`/radius-servers`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusServers(orgId) }),
  });
}

/** PUT /api/v2/radius-servers/{id} — update (blank secret keeps the stored one). */
export function useUpdateRadiusServer() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, ...body }: RadiusServerBody & { id: number }) =>
      api.put<RadiusServer>(`/radius-servers/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusServers(orgId) }),
  });
}

/** DELETE /api/v2/radius-servers/{id}. */
export function useDeleteRadiusServer() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ id: number }>(`/radius-servers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusServers(orgId) }),
  });
}

/* ---- Managed RADIUS — realms + credentials (Passpoint keystone) ----------- */

/** GET /api/v2/radius/realms — the org's managed-RADIUS realms. */
export function useRadiusRealms() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.radiusRealms(orgId),
    queryFn: ({ signal }) => api.get<{ realms: RadiusRealm[] }>(`/radius/realms`, undefined, signal),
    enabled: !!orgId,
  });
}

/** POST /api/v2/radius/realms — get-or-create a realm. */
export function useCreateRadiusRealm() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: { realm: string; label?: string; default_method?: string }) =>
      api.post<RadiusRealm>(`/radius/realms`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusRealms(orgId) }),
  });
}

/** GET /api/v2/radius/realms/{id}/credentials — credentials in a realm (active by default). */
export function useRadiusCredentials(realmId: number | null, includeInactive = false) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.radiusCredentials(orgId, realmId),
    queryFn: ({ signal }) =>
      api.get<{ credentials: RadiusCredential[] }>(
        `/radius/realms/${realmId}/credentials`, includeInactive ? { all: 1 } : undefined, signal),
    enabled: !!orgId && realmId != null,
  });
}

type IssueBody = {
  username?: string;
  method?: string;
  vlan_id?: number | null;
  expires_at?: string | null;
  issued_for?: string;
};

/** POST /api/v2/radius/realms/{id}/provision — issue a credential AND get an install profile. */
export function useProvisionGuest(realmId: number | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ platform, ...body }: IssueBody & { platform: "ios" | "android" }) =>
      api.post<ProvisionResult>(
        `/radius/realms/${realmId}/provision?platform=${platform}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusCredentials(orgId, realmId) }),
  });
}

/** DELETE /api/v2/radius/credentials/{id} — revoke a credential. */
export function useRevokeRadiusCredential(realmId: number | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ id: number; state: string }>(`/radius/credentials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.radiusCredentials(orgId, realmId) }),
  });
}

/* ------------------------------------------------------------------ *
 * Notification channels — Network > Notifications (org-scoped)
 * ------------------------------------------------------------------ */

export function useNotificationChannels() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.notifications.channels(orgId),
    queryFn: ({ signal }) => api.list<NotificationChannel>(`/notifications/channels`, undefined, signal),
    enabled: !!orgId,
  });
}

type ChannelBody = { kind?: NotificationKind; name?: string; target?: string; enabled?: boolean };

export function useCreateChannel() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: ChannelBody) => api.post<NotificationChannel>(`/notifications/channels`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications.channels(orgId) }),
  });
}

/** PATCH a channel. Omit `target` to keep the stored one (OP#1917: reads never return it). */
export function useUpdateChannel() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, ...body }: ChannelBody & { id: number }) =>
      api.patch<NotificationChannel>(`/notifications/channels/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications.channels(orgId) }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ id: number }>(`/notifications/channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications.channels(orgId) }),
  });
}

/** POST .../test — send a synthetic message; returns {ok, error}. */
export function useTestChannel() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ ok: boolean; error: string | null; channel: NotificationChannel }>(
        `/notifications/channels/${id}/test`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications.channels(orgId) }),
  });
}

/* ------------------------------------------------------------------ *
 * Radios — Wireless > Radio Settings (site-scoped)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/radios — the site's radios per band. */
export function useRadios(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.radios(orgId, siteId),
    queryFn: ({ signal }) => api.get<RadioList>(`/sites/${siteId}/radios`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST /api/v2/sites/{siteId}/radios/{band} — set channel/width/tx-power for a band. */
export function useUpdateRadio(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({
      band,
      ...body
    }: {
      band: string;
      channel?: number | "auto";
      channel_width?: number | null;
      tx_power?: number | "auto";
      multicast_rate?: number | null;
      allowed_channels?: number[];   // channel-plan: channels auto-select may use
      allow_dfs?: boolean;           // permit DFS channels in the plan
    }) => api.post<RadioFanoutResult>(`/sites/${siteId}/radios/${band}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.radios(orgId, siteId) }),
  });
}

/* ------------------------------------------------------------------ *
 * Site configuration ACTIONS — the Site "Configure" page (fan-out)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/config/actions — site-wide action catalog. */
export function useSiteConfigActions(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.configActions(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<SiteConfigActions>(`/sites/${siteId}/config/actions`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** POST .../{action_id}/preview — "N of M devices would change" + a sample diff. */
export function usePreviewSiteConfigAction(siteId: string | null) {
  return useMutation({
    mutationFn: (actionId: string) =>
      api.post<SiteActionPreview>(`/sites/${siteId}/config/actions/${actionId}/preview`),
  });
}

/** POST .../{action_id}/apply — fan-out push across the site; invalidate org scope. */
export function useApplySiteConfigAction(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (actionId: string) =>
      api.post<SiteActionApplyResult>(`/sites/${siteId}/config/actions/${actionId}/apply`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgScopeKey(orgId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * WAN watchdog (site-scoped gateway connectivity remediation)
 * ------------------------------------------------------------------ */

/** GET /api/v2/sites/{siteId}/wan-watchdog — per-gateway policy + the ladder. */
export function useSiteWanWatchdog(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.wanWatchdog(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<SiteWanWatchdog>(`/sites/${siteId}/wan-watchdog`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/**
 * PUT — validate then fan the policy across every gateway at the site.
 *
 * A 400 here is the guardrail speaking (unreachable-by-construction probe host,
 * or `watchdog-reset` with nothing but this controller to probe). The client
 * lifts the server's `error` string into ApiError.message, so the caller can and
 * SHOULD render it verbatim — the text names the specific host and the reason.
 */
export function useSaveSiteWanWatchdog(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (policy: WatchdogPolicyInput) =>
      api.put<WatchdogApplyResult>(`/sites/${siteId}/wan-watchdog`, policy),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.wanWatchdog(orgId, siteId) });
    },
  });
}

/** DELETE — remove the policy from every gateway at the site. */
export function useClearSiteWanWatchdog(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: () => api.del<WatchdogApplyResult>(`/sites/${siteId}/wan-watchdog`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sites.wanWatchdog(orgId, siteId) });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Clients
 * ------------------------------------------------------------------ */

export function useClients(params: ListParams = {}) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.clients.list(orgId, siteId, params),
    queryFn: ({ signal }) =>
      withMock<Paginated<Client>>(
        // org_id = the header customer (OP #1599). On "All sites" the network-wide view went
        // out unscoped, so an msp_admin saw every customer's clients. /clients now honours
        // ?org_id= as a NARROWING inside the caller's reach (404 for an out-of-scope org).
        () => api.list<Client>("/clients", { site_id: siteId, org_id: orgId, ...params }, signal),
        () => envelope(MOCK_CLIENTS.filter((c) => !siteId || c.site_id === siteId)),
      ),
    enabled: !!orgId,
  });
}

/* ------------------------------------------------------------------ *
 * Health worklist — drives the health-first dashboard
 * ------------------------------------------------------------------ */

/**
 * The open-alerts list for the current scope (header customer + site), optionally narrowed by
 * severity / kind. Backs both the Health page's "Needs attention" block and the Alerts page
 * every alert count links to (OP#1854).
 *
 * NO FAIL-OPEN (OP#1854 / F3): this used to turn a 404 into an empty list, so a failed call
 * rendered "All clear" next to "3 critical". Every HTTP error now surfaces as `isError` and the
 * caller shows an error state. Likewise a transport failure is an error here, never the demo
 * data withMock() would substitute: inventing alerts (or hiding real ones) on a health screen
 * is worse than saying "could not load". The demo data is used only in an explicit mocks build.
 */
export function useHealthWorklist(
  filters: { severity?: "critical" | "warn" | null; kind?: string | null; limit?: number } = {},
) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const { severity = null, kind = null, limit } = filters;
  return useQuery({
    queryKey: qk.health.worklist(orgId, siteId, severity, kind, limit),
    queryFn: ({ signal }) => {
      if (USE_MOCKS) {
        const items = MOCK_WORKLIST.filter(
          (a) => (!siteId || a.site_id === siteId) && (!severity || a.severity === severity)
            && (!kind || a.kind === kind),
        );
        return Promise.resolve<HealthWorklist>({
          ...envelope(items),
          summary: {
            total: items.length,
            critical: items.filter((a) => a.severity === "critical").length,
            warn: items.filter((a) => a.severity === "warn").length,
          },
        });
      }
      // org_id = the header customer (OP #1599); site_id = the header site (honoured since
      // OP#1854). The server validates both and never lets them widen the caller's reach.
      return api.get<HealthWorklist>(
        "/health/worklist",
        { org_id: orgId, site_id: siteId, severity, kind, limit },
        signal,
      );
    },
    enabled: !!orgId,
    // Health is the freshest thing on screen — poll it.
    refetchInterval: 30_000,
  });
}

/* ------------------------------------------------------------------ *
 * Dashboard overview — the realtime aggregate the Health screen polls
 * ------------------------------------------------------------------ */

/** A small synthesized overview for the mocks build (click-through without a backend). */
function mockOverview(): DashboardOverview {
  const now = Date.now();
  const series = Array.from({ length: 20 }, (_, i) => {
    const base = 18 - i;
    return {
      t: new Date(now - (19 - i) * 60_000).toISOString(),
      rx_bps: 4_000_000 + Math.round(Math.sin(i / 2) * 1_500_000 + i * 60_000),
      tx_bps: 1_200_000 + Math.round(Math.cos(i / 2) * 500_000),
      clients: 20 + (i % 5),
      _b: base,
    };
  }).map(({ _b, ...p }) => p);
  const last = series[series.length - 1];
  return {
    generated_at: new Date(now).toISOString(),
    devices: { total: 8, online: 7, offline: 1, by_kind: [
      { kind: "access_point", label: "Access Points", online: 5, offline: 0, total: 5 },
      { kind: "switch", label: "Switches", online: 2, offline: 1, total: 3 },
    ] },
    clients: { count: 24 },
    alerts: { critical: 0, warn: 1, total: 1 },
    health: { score: 91, tone: "healthy" },
    top_aps: [
      { serial: "OAP101-AE1A", name: "Lobby-AP", clients: 12 },
      { serial: "OAP101-BF2C", name: "Floor2-AP", clients: 8 },
    ],
    activity: { window_seconds: 3600, bucket_seconds: 60, series,
      now: { rx_bps: last?.rx_bps ?? 0, tx_bps: last?.tx_bps ?? 0, clients: 24 } },
  };
}

export function useDashboardOverview(windowSeconds = 3600) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: [...qk.dashboard.overview(orgId, siteId), windowSeconds] as const,
    queryFn: ({ signal }) =>
      withMock<DashboardOverview>(
        // org_id = the header customer (OP #1599). The endpoint narrows to it within the
        // caller's reach and 404s an out-of-scope org; without it an msp_admin on "All
        // sites" saw the whole platform's overview under one customer's name.
        () => api.get<DashboardOverview>("/dashboard/overview",
          { org_id: orgId, site_id: siteId, window: windowSeconds }, signal),
        () => mockOverview(),
      ),
    enabled: !!orgId,
    // Poll fast for the live (short) window; slow the cadence for long history ranges.
    refetchInterval: windowSeconds <= 7200 ? 8_000 : 60_000,
    refetchIntervalInBackground: false,
  });
}

/* ------------------------------------------------------------------ *
 * Resolve preview — the shared DiffView data source (Phase 2, typed now)
 * ------------------------------------------------------------------ */

/** POST /api/v2/bindings/{id}/resolve — preview flat payload + structured diff,
 *  no push. The DiffView renders the returned ConfigDiff. */
export function useResolvePreview() {
  return useMutation({
    mutationFn: (bindingId: string) =>
      api.post<{ diff: ConfigDiff; payload: unknown }>(`/bindings/${bindingId}/resolve`),
  });
}

/* ------------------------------------------------------------------ *
 * Canonical catalog objects — Networks(VLAN) / ACL / FirewallPolicy /
 * PortProfile. Generic CRUD over /api/v2/catalog/<kind> (one screen shape per
 * kind, system-design 4). Every catalog page shares these three hooks.
 * ------------------------------------------------------------------ */

/** A catalog row: the common CatalogObject fields plus kind-specific columns
 *  (vlan_id, rules, mode, …) which each page reads off the index signature. */
export interface CatalogObject {
  id: number;
  org_id: number;
  name: string;
  version: number;
  is_system: boolean;
  kind: string;
  [field: string]: unknown;
}

/** GET /api/v2/catalog/{kind} — the org's objects of one kind. */
export function useCatalog(kind: string) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.catalog.list(orgId, kind),
    queryFn: ({ signal }) => api.list<CatalogObject>(`/catalog/${kind}`, undefined, signal),
    enabled: !!orgId,
  });
}

/** POST (create) or PUT (update, id present) /api/v2/catalog/{kind}. org_id is
 *  derived server-side from auth scope, never the body. */
export function useSaveCatalogObject(kind: string) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: number }) =>
      id
        ? api.put<CatalogObject>(`/catalog/${kind}/${id}`, body)
        : api.post<CatalogObject>(`/catalog/${kind}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.catalog.list(orgId, kind) }),
  });
}

/** DELETE /api/v2/catalog/{kind}/{id} (409 for is_system objects — clone, don't delete). */
export function useDeleteCatalogObject(kind: string) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ deleted: boolean }>(`/catalog/${kind}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.catalog.list(orgId, kind) }),
  });
}

/* ------------------------------------------------------------------ *
 * Topology — Network-wide > Topology (LLDP-derived graph)
 * ------------------------------------------------------------------ */

/** One box on the map (services/topology_graph, OP#1857). */
export interface TopoNode {
  id: string; label: string; kind: string; serial: string | null;
  /** null = UNKNOWN (an upstream switch we don't manage): never drawn as online. */
  ip: string | null; online: boolean | null; health: string; managed: boolean;
  /** Lifecycle state (online | offline | dormant | provisioning | unknown). */
  state?: string;
  /** The node this one's LLDP uplink points at (lays children out under their parent). */
  parent?: string | null;
  /** lldp = neighbour known; unknown = no LLDP neighbour; root = gateway; null = unmanaged. */
  uplink?: "lldp" | "unknown" | "root" | null;
  last_seen?: string | null;
}
/** One LLDP link: device -> its uplink neighbour, with the snapshot's age. */
export interface TopoEdge {
  source: string; target: string; port: string | null; vlan: number | null; speed_mbps: number | null;
  /** When the neighbour was last confirmed (derived; see models/device.lldp_as_of). */
  as_of?: string | null;
  /** True when the link is history (reporter offline / not re-confirmed): drawn dashed. */
  stale?: boolean;
  /** True when the neighbour resolved to a device at ANOTHER site of the org because the
   *  reporter's own site had none (services/topology_graph rule 6, OP#1919): labelled. */
  cross_site?: boolean;
}
export interface Topology {
  nodes: TopoNode[]; edges: TopoEdge[];
  counts: { nodes: number; edges: number; managed: number; external: number; stale?: number; uplink_unknown?: number; cross_site?: number };
}

/** GET /api/v2/topology — the LLDP-derived L2 graph for the current scope. */
export function useTopology() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.topology(orgId, siteId),
    queryFn: ({ signal }) =>
      // org_id = the header customer (OP#1625): the server only NARROWS on it.
      api.get<Topology>("/topology", { org_id: orgId, site_id: siteId }, signal),
    enabled: !!orgId,
    refetchInterval: 60_000,
  });
}

/* ------------------------------------------------------------------ *
 * RF Spectrum — Wireless > RF Spectrum (live per-AP radios)
 * ------------------------------------------------------------------ */

export interface RfRadio {
  device_serial: string; device_name: string; band: string | null;
  channel: number | null; channel_width: number | null; tx_power: number | null;
  utilization_pct: number | null; noise: number | null; temperature: number | null; clients: number;
}

/** GET /api/v2/rf — live per-AP radio rows for the current scope. */
export function useRf() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: qk.rf(orgId, siteId),
    queryFn: ({ signal }) =>
      // org_id = the header customer (OP#1625): the server only NARROWS on it.
      api.list<RfRadio>("/rf", { org_id: orgId, site_id: siteId }, signal),
    enabled: !!orgId,
    refetchInterval: 30_000,
  });
}

/** One Auto-RF channel recommendation for a radio. */
export interface RfRecommendation {
  serial: string; name: string; band: string | null;
  current: number | null; recommended: number;
  current_score: number | null; recommended_score: number;
  neighbors_considered: number; suggest_change: boolean;
}

/** GET /api/v2/rf/recommendations — quietest-channel recommendation per radio. */
export function useRfRecommendations() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useQuery({
    queryKey: [...qk.rf(orgId, siteId), "recommendations"] as const,
    queryFn: ({ signal }) =>
      api.get<{ items: RfRecommendation[]; count: number }>(
        // org_id = the header customer (OP#1625): the server only NARROWS on it.
        "/rf/recommendations", { org_id: orgId, site_id: siteId }, signal),
    enabled: !!orgId,
    refetchInterval: 60_000,
  });
}

/** POST /api/v2/devices/{serial}/radios/{band} — apply a channel to ONE AP (Auto-RF apply). */
export function useApplyDeviceRadio() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return useMutation({
    mutationFn: ({ serial, band, channel }: { serial: string; band: string; channel: number }) =>
      api.post<{ changed: boolean; pushed: boolean; band: string }>(
        `/devices/${serial}/radios/${band}`, { channel }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.rf(orgId, siteId) });
      qc.invalidateQueries({ queryKey: [...qk.rf(orgId, siteId), "recommendations"] });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Firmware — Network > Firmware (images + rollout status)
 * ------------------------------------------------------------------ */

export interface FirmwareImage {
  id: number; version: string; device_type: string; checksum_sha256: string;
  file_size: number; release_notes: string | null; is_stable: boolean; created_at: string;
  /**
   * The CURRENT approval behind `is_stable` — who marked this build fit for the stable
   * channel, and when. Null on images approved before the trail existed, and on any image
   * that is not currently approved (a withdrawal clears both). The history of grants and
   * withdrawals lives in audit_events, not here.
   */
  approved_by: string | null; approved_at: string | null;
  /** "upload" = served by the controller; "url" = vendor-hosted (the AP fetches it directly). */
  source: "upload" | "url"; url: string | null;
}
export interface FirmwareUpdate {
  id: number; device_id: number; device_serial: string | null; device_name: string | null;
  firmware_id: number; version: string | null; device_type: string | null;
  status: string | null; progress: number; error_message: string | null;
  created_at: string | null; started_at: string | null; completed_at: string | null;
}

/** GET /api/v2/firmware/images — the org's uploaded firmware binaries. */
export function useFirmwareImages() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.firmware.images(orgId),
    queryFn: ({ signal }) => api.list<FirmwareImage>("/firmware/images", undefined, signal),
    enabled: !!orgId,
  });
}

/** POST /api/v2/firmware/images/by-url — register a VENDOR-HOSTED image (e.g. a TIP JFrog URL) as a
 *  managed baseline without uploading a file. device_type must equal the device model string. */
export function useRegisterFirmwareUrl() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: {
      version: string; device_type: string; url: string;
      sha256?: string; release_notes?: string; is_stable?: boolean;
    }) => api.post<FirmwareImage>("/firmware/images/by-url", body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.firmware.images(orgId) }),
  });
}

/** GET /api/v2/firmware/updates — recent firmware update attempts (rollout status). */
export function useFirmwareUpdates() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.firmware.updates(orgId),
    queryFn: ({ signal }) => api.list<FirmwareUpdate>("/firmware/updates", undefined, signal),
    enabled: !!orgId,
    refetchInterval: 30_000,
  });
}

/* ------------------------------------------------------------------ *
 * PMS Integration — Hospitality > PMS Integration (per-site)
 * ------------------------------------------------------------------ */

export interface PmsIntegration {
  site_id: number; enabled: boolean; vendor: string | null; host: string | null;
  port: number | null; site_code: string | null; username: string | null;
  has_credential: boolean; updated_at: string | null;
}

/** GET /api/v2/sites/{siteId}/pms — the site's PMS settings (blank default when unset). */
export function usePms(siteId: string | null) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.sites.pms(orgId, siteId),
    queryFn: ({ signal }) => api.get<PmsIntegration>(`/sites/${siteId}/pms`, undefined, signal),
    enabled: !!siteId && !!orgId,
  });
}

/** PUT /api/v2/sites/{siteId}/pms — upsert the site's PMS settings. */
export function useSavePms(siteId: string | null) {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: {
      enabled: boolean; vendor?: string | null; host?: string | null;
      port?: number | null; site_code?: string | null; username?: string | null; api_key?: string;
    }) => api.put<PmsIntegration>(`/sites/${siteId}/pms`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites.pms(orgId, siteId) }),
  });
}

/* ---------------------------------------------------------------------------
 * Operators — the people who can log in.
 *
 * Identity lives in the IdP; Meridian holds no user rows, so every one of these
 * is a round trip. They are deliberately NOT polled: an operator list changes
 * when a human changes it, and each read costs an IdP call.
 * ------------------------------------------------------------------------- */

/**
 * GET /api/v2/operators?org_id= — operators the caller may see, NARROWED to the
 * customer selected in the header (OP#1859). Without org_id a platform_admin got
 * the whole realm, so CyberTan's Team page listed GTK staff and another
 * customer's test accounts. The server validates the org (404 out of reach) and
 * never widens a scoped caller, so sending it is always safe.
 */
export function useOperators() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.operators(orgId),
    queryFn: ({ signal }) =>
      api.get<OperatorList>("/operators", { org_id: orgId }, signal),
    enabled: !!orgId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * GET /api/v2/operators/preflight — can operators be managed at all?
 *
 * Checked BEFORE the screen offers to create anyone. A misconfigured identity
 * provider silently discards the attribute carrying site assignment, so an
 * operator would be created looking correct and confined to nothing.
 */
export function useOperatorPreflight() {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.operatorPreflight(orgId),
    queryFn: ({ signal }) =>
      api.get<OperatorPreflight>("/operators/preflight", undefined, signal),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export interface OperatorBody {
  username: string;
  /** Email them a link to set their own password instead of an admin choosing one.
   *  Refused (409) until the server reports invite_email_available. */
  invite?: boolean;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  password?: string;
  temporary?: boolean;
  role: Role;
  org_ids: number[];
  site_ids: number[];
}

/**
 * Refresh both people lists after any operator write: the customer Team page AND Platform
 * staff (2026-10-15). A person can move between them (an edit that adds or removes their last
 * customer, or the test-account flag), so refreshing only one would leave a stale row.
 */
function invalidateTeamLists(qc: ReturnType<typeof useQueryClient>, orgId: string | null) {
  void qc.invalidateQueries({ queryKey: qk.operators(orgId) });
  void qc.invalidateQueries({ queryKey: qk.platformStaff() });
}

/**
 * GET /api/v2/operators/platform-staff — platform_admin ONLY (403 for everyone else, so the
 * caller passes `enabled` = is_platform_admin and nobody else ever sends it). GTK staff with
 * no customer, plus every test account, each with `is_test`.
 */
export function usePlatformStaff(enabled: boolean) {
  return useQuery({
    queryKey: qk.platformStaff(),
    queryFn: ({ signal }) =>
      api.get<OperatorList>("/operators/platform-staff", undefined, signal),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** PUT /api/v2/operators/{id}/test-account — flag / unflag a test account (platform_admin). */
export function useSetTestAccount() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, test }: { id: string; test: boolean }) =>
      api.put<Operator>(`/operators/${id}/test-account`, { test }),
    onSuccess: () => invalidateTeamLists(qc, orgId),
  });
}

/** POST /api/v2/operators — create a login. */
export function useCreateOperator() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: OperatorBody) => api.post<Operator>("/operators", body),
    // The variables carry the temporary password. gcTime 0 drops the finished mutation from
    // the cache as soon as the dialog unmounts instead of holding it for 5 minutes.
    gcTime: 0,
    onSuccess: () => invalidateTeamLists(qc, orgId),
  });
}

/** PATCH /api/v2/operators/{id} — change role, buildings, contact or enabled. */
export function useUpdateOperator() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    // When the change takes access away, the server signs them out first and says so
    // (`signed_out`, plus a `warning` if the sign-out was incomplete).
    mutationFn: ({ id, ...body }: Partial<OperatorBody> & { id: string; enabled?: boolean }) =>
      api.patch<Operator & Partial<OperatorSignOut> & { signed_out?: boolean }>(
        `/operators/${id}`,
        body,
      ),
    onSuccess: () => invalidateTeamLists(qc, orgId),
  });
}

/**
 * POST /api/v2/operators/{id}/password — set a password on someone's behalf.
 * The response never echoes the password; it reports which first-sign-in steps are now
 * pending (UPDATE_PASSWORD when temporary, CONFIGURE_TOTP when they have no authenticator).
 */
export function useResetOperatorPassword() {
  return useMutation({
    // Same as useCreateOperator: never keep the password-bearing variables around.
    gcTime: 0,
    mutationFn: ({ id, password, temporary }: { id: string; password: string; temporary: boolean }) =>
      // OP#1594: the server also signs them out everywhere (Meridian, Keycloak, open terminals).
      api.post<{ ok: boolean; temporary: boolean; required_actions: string[]; signed_out?: boolean } &
        Partial<OperatorSignOut>>(
        `/operators/${id}/password`,
        { password, temporary },
      ),
  });
}

/** DELETE /api/v2/operators/{id} — remove a login entirely. The server signs them out of
 *  every session first; `warning` is set when that sign-out was incomplete. */
export function useDeleteOperator() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean } & OperatorSignOut>(`/operators/${id}`),
    onSuccess: () => invalidateTeamLists(qc, orgId),
  });
}

/* ---------------------------------------------------------------------------------------
 * NETWORK USERS — the people who connect (Organization > Users).
 *
 * Every mutation here invalidates the whole network-users key prefix rather than the one
 * search that produced the row. Grants can change which searches a person appears in, so
 * patching a single cached list would leave the others showing a stale answer to the one
 * question this screen exists to answer: who can get on the network here.
 * ------------------------------------------------------------------------------------- */

/** Every cached network-user list for this customer, regardless of search term. */
function invalidateNetworkUsers(qc: ReturnType<typeof useQueryClient>, orgId: string | null) {
  return qc.invalidateQueries({ queryKey: qk.networkUsers(orgId).slice(0, 3) });
}

/** GET /api/v2/network-users — people with access to a building the caller can see. */
export function useNetworkUsers(q?: string) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: qk.networkUsers(orgId, q),
    queryFn: ({ signal }) =>
      // org_id = the header customer (OP #1599): the screen is "Org > Users" and writes grants
      // against currentOrgId, but the list went out unscoped, so an msp_admin saw every
      // customer's residents. ?org_id= only narrows inside the caller's reach (network_users.py).
      api.get<NetworkUserList>("/network-users", { org_id: orgId, q }, signal),
    enabled: !!orgId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export interface NetworkUserBody {
  email?: string;
  display_name?: string | null;
  notes?: string | null;
  state?: string;
  /** Where they may connect. Omit entirely to leave existing access untouched. */
  grants?: Array<{ org_id: number; all_sites?: boolean; site_ids?: number[] }>;
}

/**
 * POST /api/v2/network-users — add a person, or grant an existing one access here.
 *
 * 201 for someone new, 200 when the email already existed and this ADDED access. The
 * second case is not an error: the same person working at two customers is the reason
 * this model exists, and "that email is taken" would both mislead and disclose that they
 * exist at a customer the caller may not be entitled to know about.
 */
export function useCreateNetworkUser() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (body: NetworkUserBody) => api.post<NetworkUser>("/network-users", body),
    onSuccess: () => invalidateNetworkUsers(qc, orgId),
  });
}

/** PATCH /api/v2/network-users/{id} — name, notes, access, or suspend/reactivate. */
export function useUpdateNetworkUser() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: ({ id, ...body }: NetworkUserBody & { id: number }) =>
      api.patch<NetworkUser>(`/network-users/${id}`, body),
    onSuccess: () => invalidateNetworkUsers(qc, orgId),
  });
}

/**
 * DELETE /api/v2/network-users/{id} — remove the person.
 *
 * Their RADIUS credentials are DETACHED, not deleted: one may still be loaded on a live
 * device, and revoking it is a separate decision with its own blast radius.
 */
export function useDeleteNetworkUser() {
  const qc = useQueryClient();
  const orgId = useSession((s) => s.currentOrgId);
  return useMutation({
    mutationFn: (id: number) => api.del<{ ok: boolean }>(`/network-users/${id}`),
    onSuccess: () => invalidateNetworkUsers(qc, orgId),
  });
}

/**
 * POST /api/v2/devices/import — load names and placement from a workbook.
 *
 * Deliberately NOT a query: this is a two-step operator action (plan, then
 * apply), and a cached plan would be stale the moment anything else changed a
 * device. Call it once for the plan and again with apply:true.
 */
export function useImportDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { csv: string; apply?: boolean; site_id?: number | null }) =>
      api.post<ImportResult>("/devices/import", body),
    onSuccess: (res) => {
      // Only a write invalidates; a dry run changed nothing.
      // Invalidate by PREFIX: qk.devices is a namespace of key builders, and an import
      // can touch any site, so no single list key covers it.
      if (res.applied) qc.invalidateQueries({ queryKey: ["v2"] });
    },
  });
}

/** POST /api/v2/operators/{id}/invite — re-send the set-your-password link. */
export function useResendOperatorInvite() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; sent_to: string }>(`/operators/${id}/invite`, {}),
  });
}

/* ------------------------------------------------------------------ *
 * Diagnostics (device sheet > Diagnostics tab). Server: api/v2/diagnostics.py.
 * The catalogue the server returns is ALREADY filtered to what this viewer may run; runs are
 * mutations (each one is an audited device session), never queries, so nothing re-runs on
 * focus or remount.
 * ------------------------------------------------------------------ */

/** GET /api/v2/diagnostics/catalogue?kind= — entries this viewer may run for a device kind. */
export function useDiagnosticsCatalogue(kind: string | undefined) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: ["v2", orgId ?? "-", "diagnostics", "catalogue", kind ?? "-"] as const,
    queryFn: ({ signal }) =>
      api.get<DiagnosticCatalogue>("/diagnostics/catalogue", { kind }, signal),
    enabled: !!kind,
    staleTime: 5 * 60_000, // role-derived; changes only with the session
  });
}

/** POST /api/v2/devices/{serial}/diagnostics/{name} — run one named, read-only diagnostic. */
export function useRunDiagnostic(serial: string) {
  return useMutation({
    mutationFn: ({ name, params }: { name: string; params?: Record<string, string> }) =>
      api.post<DiagnosticResult>(`/devices/${serial}/diagnostics/${name}`, { params: params ?? {} }),
  });
}

/** POST /api/v2/devices/{serial}/diagnostics/bundle — the sanitized support bundle file. */
export function useDiagnosticsBundle(serial: string) {
  return useMutation({
    mutationFn: () => api.download(`/devices/${serial}/diagnostics/bundle`),
  });
}
